import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiX, FiRefreshCw, FiActivity, FiClock, FiInfo, FiLock, FiBarChart2, FiLink, FiExternalLink, FiChevronDown } from 'react-icons/fi';
import PanelChart from './PanelChart';
import WaitOthersPicker from './WaitOthersPicker';
import { buildWaitClassColors, OTHERS_COLOR } from './waitClassColors';
import './DbmsPerfAnalysisModal.css';
import AppModal from '../common/AppModal';
import SqlSourceView from './SqlSourceView';
import { QueryViewButton, QueryViewDialog } from './QueryView';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import PerfSettingsPanel from './PerfSettingsPanel';
import { resolveMetrics, splitByUnit, canShow, MAX_SHOWN } from '../../utils/waitMetricMap';

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const PERF_API = `${API_ROOT}/sql-developer/monitoring/dbms-perf`;
// SQL 원문 — 자원 분석 창과 같은 라우트(ashCommon.buildSqlTextQuery 한 곳)를 쓴다
const SQL_TEXT_API = `${API_ROOT}/sql-developer/monitoring/resource-analysis/sql-text`;

const getWindowOptions = (t) => [
    { value: 30, label: t('monitoring:range.min30') },
    { value: 60, label: t('monitoring:range.hour1') },
    { value: 120, label: t('monitoring:range.hour2') },
];
const getDaysOptions = (t) => [
    { value: 1, label: t('monitoring:range.day1') },
    { value: 3, label: t('monitoring:range.day3') },
    { value: 7, label: t('monitoring:range.day7') },
    { value: 14, label: t('monitoring:range.day14') },
];
// 아래 그래프에 무엇을 볼지 — 위는 늘 대기이벤트다(이 탭의 출발점). 시스템 메트릭이 기본이고, SQL·세션·모듈은 같은 ASH 를 그 기준으로 다시 센 것
const getLowerModes = (t) => [
    { value: 'metric', label: t('monitoring:perf.lower.metric') },
    { value: 'sql', label: 'SQL' },
    { value: 'session', label: t('monitoring:perf.session') },
    { value: 'module', label: t('monitoring:perf.module') },
    // 관련 설정(2026-10-16) — 그 대기와 함께 볼 파라미터·DB 설정·REDO 현황(서버 waitSettings 의 표)
    { value: 'settings', label: t('monitoring:perf.lower.settings') },
];
// Oracle 표준 wait class (V$EVENT_NAME.WAIT_CLASS 고정값) — 그룹 필터용.
const WAIT_CLASSES = [
    'Administrative', 'Application', 'Cluster', 'Commit', 'Concurrency',
    'Configuration', 'Idle', 'Network', 'Other', 'Scheduler', 'System I/O', 'User I/O',
];

// 대기 그룹 → 설명 키(monitoring:perf.wclass.*) — 범례 툴팁(2026-10-27). 'CPU' 는 ASH 의 ON CPU 묶음(서버가 붙이는 이름)
const WAIT_CLASS_DESC_KEY = {
    'User I/O': 'userIo', 'System I/O': 'systemIo', Concurrency: 'concurrency', Application: 'application', Commit: 'commit',
    Configuration: 'configuration', Cluster: 'cluster', Network: 'network', Other: 'other', Scheduler: 'scheduler',
    Administrative: 'administrative', Idle: 'idle', CPU: 'cpu',
};
// 범례 「설명 보기」 — 위(대기)·아래(메트릭·SQL·세션·모듈) 따로, 이 브라우저에 기억(기본 켬)
const HINT_PREF_KEY = 'livesql_perf_legend_hint';
const readHintPref = (which) => { try { return localStorage.getItem(`${HINT_PREF_KEY}_${which}`) !== '0'; } catch (_e) { return true; } };
const writeHintPref = (which, on) => { try { localStorage.setItem(`${HINT_PREF_KEY}_${which}`, on ? '1' : '0'); } catch (_e) { /* 기억 못 해도 동작한다 */ } };
const SQL_ID_LIKE = /^[a-z0-9]{13}$/;

// 대기 그룹 고르기(2026-10-16 요청) — 튜닝에서 주로 보는 그룹을 먼저 보이고, 나머지는 「더 보기」 를 골라야 목록에 붙는다.
// 순서는 흔히 문제가 되는 순(AWR Top 이벤트에 자주 오르는 그룹). 둘을 합치면 WAIT_CLASSES 와 같아야 한다(테스트가 본다)
const MAIN_WAIT_CLASSES = ['User I/O', 'System I/O', 'Concurrency', 'Application', 'Commit', 'Configuration', 'Cluster', 'Network'];
const MORE_WAIT_CLASSES = WAIT_CLASSES.filter(w => !MAIN_WAIT_CLASSES.includes(w));
const MORE_VALUE = '__more__';

function WaitClassSelect({ value, onChange, t }) {
    const [expanded, setExpanded] = useState(false);
    // 고른 값이 「그 외」 에 있으면 접혀 있어도 보여야 한다(안 그러면 선택 상자가 빈 값처럼 보인다)
    const extra = !expanded && MORE_WAIT_CLASSES.includes(value) ? [value] : [];
    const onPick = (e) => {
        if (e.target.value !== MORE_VALUE) { onChange(e.target.value); return; }
        setExpanded(true);
        // 펼친 목록을 곧바로 다시 연다(지원하는 브라우저만 — 안 되면 한 번 더 누르면 된다)
        const el = e.target;
        setTimeout(() => { try { if (el.showPicker) el.showPicker(); } catch (_e) { /* 사용자 동작 밖이면 열 수 없다 */ } }, 0);
    };
    return (
        <select className="dbmsperf-select" value={value} onChange={onPick} title={t('monitoring:perf.waitGroupHint')}>
            <option value="">{t('monitoring:perf.allWaitGroups')}</option>
            {MAIN_WAIT_CLASSES.map(w => <option key={w} value={w}>{w}</option>)}
            {extra.map(w => <option key={w} value={w}>{w}</option>)}
            {expanded ? (
                <optgroup label={t('monitoring:perf.waitGroupMore')}>
                    {MORE_WAIT_CLASSES.map(w => <option key={w} value={w}>{w}</option>)}
                </optgroup>
            ) : (
                <option value={MORE_VALUE}>{t('monitoring:perf.waitGroupShowMore', { count: MORE_WAIT_CLASSES.length - extra.length })}</option>
            )}
        </select>
    );
}
WaitClassSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

/**
 * 대기 검색(2026-10-16 요청) — 글을 넣으면 상위 4개 규칙 대신 그 글이 들어간 대기만 계열로 보인다(최대 12, 나머지는 기타).
 * 치는 동안 매번 조회하지 않게 잠깐 기다렸다 넘긴다. 비우면 다시 상위 4개
 */
/**
 * 그래프 제목 줄(설명 포함)은 한 줄 높이로 고정한다(2026-10-16) — 설명이 길어 두 줄이 되면 그래프가 아래로 밀렸다. 넘치는 글은 「…」 로 줄이고,
 * 마우스를 올리면 잘린 경우에만 전체 글을 툴팁으로 보인다
 */
const showFullTitle = (e) => {
    const el = e.currentTarget;
    el.title = el.scrollWidth > el.clientWidth ? el.textContent.replace(/\s+/g, ' ').trim() : '';
};

function WaitSearch({ value, onChange, suggestions, t }) {
    const [text, setText] = useState(value || '');
    // 후보 목록은 화면이 직접 그린다(2026-10-16) — 브라우저의 datalist 목록은 앱 밖의 창이라 다크모드를 따르지 않고(흰 목록) 모양도 바꿀 수 없었다
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(-1);
    const listRef = useRef(null);
    const listId = useMemo(() => `dbmsperf-ev-${Math.random().toString(36).slice(2, 8)}`, []);
    useEffect(() => { setText(value || ''); }, [value]);
    useEffect(() => {
        const id = setTimeout(() => { if (text.trim() !== (value || '')) onChange(text.trim()); }, 400);
        return () => clearTimeout(id);
    }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
    const q = text.trim().toLowerCase();
    const items = useMemo(() => (suggestions || []).filter(ev => !q || String(ev).toLowerCase().includes(q)).slice(0, 300), [suggestions, q]);
    useEffect(() => { setHi(-1); }, [q, open]);
    useEffect(() => {
        if (hi < 0 || !listRef.current) return;
        const el = listRef.current.children[hi];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [hi]);
    // 목록에서 고른 것은 정확한 대기 이름이다 — 그 대기만(exact)
    const pick = (ev) => { setText(ev); onChange(ev, { exact: true }); setOpen(false); };
    const clear = () => { setText(''); onChange(''); setOpen(false); };
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setHi(h => Math.min(items.length - 1, h + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(-1, h - 1)); }
        else if (e.key === 'Enter') { if (open && hi >= 0 && items[hi]) pick(items[hi]); else { onChange(text.trim()); setOpen(false); } }
        else if (e.key === 'Escape') { if (open) setOpen(false); else clear(); }
    };
    return (
        <span className="dbmsperf-search">
            <input
                className="dbmsperf-search-input" value={text}
                role="combobox" aria-expanded={open ? 'true' : 'false'} aria-controls={listId} aria-autocomplete="list"
                placeholder={t('monitoring:perf.search.ph')} aria-label={t('monitoring:perf.search.ph')} title={t('monitoring:perf.search.hint')}
                onChange={(e) => { setText(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onKeyDown={onKeyDown}
            />
            {/* 꺾쇠 — 누르면 목록을 열고 닫는다. 입력 칸의 포커스를 빼앗지 않게 mousedown 에서 막는다 */}
            <button type="button" className="dbmsperf-search-caret" tabIndex={-1}
                onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); e.currentTarget.parentNode.querySelector('input').focus(); }}
                aria-label={t('monitoring:perf.search.list')} title={t('monitoring:perf.search.list')}>
                <FiChevronDown size={13} strokeWidth={3} aria-hidden="true" />
            </button>
            {text ? <button type="button" className="dbmsperf-search-clear" onMouseDown={(e) => e.preventDefault()} onClick={clear} title={t('monitoring:perf.search.clear')} aria-label={t('monitoring:perf.search.clear')}>×</button> : null}
            {open && items.length > 0 && (
                <ul className="dbmsperf-search-list" role="listbox" id={listId} ref={listRef}
                    // 목록 안(스크롤바 포함)을 눌러도 입력 칸의 포커스를 빼앗지 않는다 — 빼앗기면 blur 로 목록이 닫혀 스크롤바를 끌 수 없었다
                    onMouseDown={(e) => e.preventDefault()}>
                    {items.map((ev, idx) => (
                        <li key={ev} role="option" aria-selected={idx === hi}
                            className={`dbmsperf-search-item${idx === hi ? ' on' : ''}${ev === value ? ' cur' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(ev); }}
                            onMouseEnter={() => setHi(idx)}>
                            {ev}
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
}
WaitSearch.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired, suggestions: PropTypes.array, t: PropTypes.func.isRequired };

// 시간대 없는 글(`YYYY-MM-DDTHH:MI:SS` — DB 서버의 벽시계)을 ms 로. 브라우저가 자기 시간대로 읽으므로 **자릿수가 그대로** 축에 찍힌다
const wallMs = (text) => { const n = text ? new Date(text).getTime() : NaN; return Number.isFinite(n) ? n : null; };
/** 가로축 범위 [지금 − 구간, 지금] — 위아래 두 그래프에 **같은 값**을 준다(서버가 준 DB 의 지금에서 나온다) */
// ms → DB 시계의 시간대 없는 글(wallMs 의 반대) — 그래프에서 끌어 고른 구간을 서버에 그대로 돌려준다
const wallText = (ms) => {
    const d = new Date(ms);
    const p2 = (v) => String(v).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
};
const hms = (wall) => (wall ? String(wall).slice(11, 19) : '');
// 락 대기의 기본 락 종류 — 행 락(TX)과 테이블 락(TM). 나머지는 고를 때만(2026-10-14)
const DEFAULT_LOCK_TYPES = ['TX', 'TM'];
const domainOf = (dbNow, minutes) => { const end = wallMs(dbNow); return end == null ? undefined : [end - minutes * 60 * 1000, end]; };
/** 서버가 실제로 쓴 구간(range) → 가로축 범위 · 길이(분). 전체 기간·끌어 고른 구간은 이것으로만 정해진다 */
const spanDomain = (sp) => { const a = sp && wallMs(sp.fromT); const b = sp && wallMs(sp.toT); return a != null && b != null && b > a ? [a, b] : undefined; };
const spanMinutes = (sp) => { const d = spanDomain(sp); return d ? Math.max(1, Math.round((d[1] - d[0]) / 60000)) : null; };
/** 끌어 고른 구간의 표시 — 날짜는 월-일까지 */
const rangeLabel = (r) => `${r.fromT.slice(5, 16).replace('T', ' ')} ~ ${r.toT.slice(5, 16).replace('T', ' ')}`;
// 너무 좁게 끈 것은 고르기가 아니다(손떨림) — 실시간 1분, 이력 1시간(스냅샷 간격)보다 짧으면 버린다
const MIN_DRAG_MS = { rt: 60 * 1000, hist: 60 * 60 * 1000 };

// GV$SYSMETRIC_HISTORY / DBA_HIST_SYSMETRIC_SUMMARY 는 딕셔너리에 설명 컬럼이 없어
// 목록에서 이름만 봐서는 뜻을 짐작하기 어렵다. 자주 쓰는 메트릭 위주로 간단 설명을 붙인다.
// 카탈로그에 있지만 여기 없는 메트릭은 "설명 없음"으로 표시된다(치명적이지 않음).
const getMetricDescriptions = (t) => ({
    'Host CPU Utilization (%)': t('monitoring:metric.hostCpu'),
    'Average Active Sessions': t('monitoring:metric.aas'),
    'Database CPU Time Ratio': t('monitoring:metric.dbCpuRatio'),
    'Database Wait Time Ratio': t('monitoring:metric.dbWaitRatio'),
    'SQL Service Response Time': t('monitoring:metric.sqlResp'),
    'Response Time Per Txn': t('monitoring:metric.respPerTxn'),
    'Executions Per Sec': t('monitoring:metric.execPerSec'),
    'User Transaction Per Sec': t('monitoring:metric.txnPerSec'),
    'User Commits Per Sec': t('monitoring:metric.commitPerSec'),
    'User Rollbacks Per Sec': t('monitoring:metric.rollbackPerSec'),
    'User Calls Per Sec': t('monitoring:metric.callPerSec'),
    'CPU Usage Per Sec': t('monitoring:metric.cpuPerSec'),
    'CPU Usage Per Txn': t('monitoring:metric.cpuPerTxn'),
    'Physical Reads Per Sec': t('monitoring:metric.physReads'),
    'Physical Writes Per Sec': t('monitoring:metric.physWrites'),
    'Logical Reads Per Sec': t('monitoring:metric.logicalReads'),
    'Buffer Cache Hit Ratio': t('monitoring:metric.bufferHit'),
    'Library Cache Hit Ratio': t('monitoring:metric.libCacheHit'),
    'Row Cache Hit Ratio': t('monitoring:metric.rowCacheHit'),
    'Shared Pool Free %': t('monitoring:metric.sharedPoolFree'),
    'PGA Cache Hit %': t('monitoring:metric.pgaHit'),
    'Total Parse Count Per Sec': t('monitoring:metric.parsePerSec'),
    'Hard Parse Count Per Sec': t('monitoring:metric.hardParsePerSec'),
    'Network Traffic Volume Per Sec': t('monitoring:metric.networkPerSec'),
    'Enqueue Timeouts Per Sec': t('monitoring:metric.enqueueTimeout'),
    'GC CR Block Received Per Second': t('monitoring:metric.racCrReceive'),
    'Global Cache Blocks Corrupted': t('monitoring:metric.racCorrupt'),
});

// V$EVENT_NAME 도 설명 컬럼이 없어 정보 아이콘용으로 자주 보는 대기이벤트를 정리해둔다.
// 대기 그룹(WAIT_CLASSES) 기준으로 묶어 보여준다.
const getWaitEventInfo = (t) => [
    { event: 'db file sequential read', waitClass: 'User I/O', description: t('monitoring:wait.dbFileSeqRead') },
    { event: 'db file scattered read', waitClass: 'User I/O', description: t('monitoring:wait.dbFileScatRead') },
    { event: 'db file parallel read', waitClass: 'User I/O', description: t('monitoring:wait.dbFileParaRead') },
    { event: 'direct path read', waitClass: 'User I/O', description: t('monitoring:wait.directPathRead') },
    { event: 'direct path write', waitClass: 'User I/O', description: t('monitoring:wait.directPathWrite') },
    { event: 'log file parallel write', waitClass: 'System I/O', description: t('monitoring:wait.logFileParaWrite') },
    { event: 'db file parallel write', waitClass: 'System I/O', description: t('monitoring:wait.dbFileParaWrite') },
    { event: 'control file parallel write', waitClass: 'System I/O', description: t('monitoring:wait.controlFileParaWrite') },
    { event: 'log file sync', waitClass: 'Commit', description: t('monitoring:wait.logFileSync') },
    { event: 'buffer busy waits', waitClass: 'Concurrency', description: t('monitoring:wait.bufferBusy') },
    { event: 'latch free', waitClass: 'Concurrency', description: t('monitoring:wait.latch') },
    { event: 'library cache lock', waitClass: 'Concurrency', description: t('monitoring:wait.libraryCacheLock') },
    { event: 'library cache pin', waitClass: 'Concurrency', description: t('monitoring:wait.libraryCachePin') },
    { event: 'row cache lock', waitClass: 'Concurrency', description: t('monitoring:wait.rowCacheLock') },
    { event: 'cursor: pin S wait on X', waitClass: 'Concurrency', description: t('monitoring:wait.cursorPinS') },
    { event: 'enq: TX - row lock contention', waitClass: 'Application', description: t('monitoring:wait.enqTx') },
    { event: 'enq: TM - contention', waitClass: 'Application', description: t('monitoring:wait.enqTm') },
    { event: 'enq: HW - contention', waitClass: 'Configuration', description: t('monitoring:wait.enqHw') },
    { event: 'log file switch completion', waitClass: 'Configuration', description: t('monitoring:wait.logFileSwitch') },
    { event: 'log buffer space', waitClass: 'Configuration', description: t('monitoring:wait.logBufferSpace') },
    { event: 'gc cr block busy', waitClass: 'Cluster', description: t('monitoring:metric.racCrLatency') },
    { event: 'gc buffer busy acquire', waitClass: 'Cluster', description: t('monitoring:metric.racBufferBusy') },
    { event: 'gc current block busy', waitClass: 'Cluster', description: t('monitoring:metric.racCurrentLatency') },
    { event: 'PX Deq: Execute Reply', waitClass: 'Other', description: t('monitoring:wait.pxDeq') },
    { event: 'PX Deq: Parse Reply', waitClass: 'Other', description: t('monitoring:wait.pxParse') },
    { event: 'sort wait', waitClass: 'Other', description: t('monitoring:wait.sortSegment') },
    { event: 'SQL*Net message from client', waitClass: 'Idle', description: t('monitoring:wait.sqlnetClient') },
    { event: 'SQL*Net message to client', waitClass: 'Network', description: t('monitoring:wait.sqlnetToClient') },
    { event: 'SQL*Net more data to client', waitClass: 'Network', description: t('monitoring:wait.sqlnetMore') },
];

// 락 대기 트리 노드의 락 종류 배지/필터용 설명. 백엔드가 event 로부터 뽑아 보내는
// node.lockType 값과 키를 맞춘다(backend/modules/monitoring/dbmsPerfRouter.js 의
// deriveLockType 참고 — enq: XX 형태는 V$LOCK.TYPE 코드 그대로, 나머지는 자체 분류).
const getLockTypeDescriptions = (t) => ({
    TX: t('monitoring:lock.tx'),
    TM: t('monitoring:lock.tm'),
    UL: t('monitoring:lock.ul'),
    HW: t('monitoring:wait.enqHwShort'),
    TS: t('monitoring:lock.ts'),
    TC: t('monitoring:lock.tt'),
    ST: t('monitoring:lock.st'),
    JQ: t('monitoring:lock.jq'),
    CF: t('monitoring:lock.cf'),
    DL: t('monitoring:lock.dl'),
    BUFFER: t('monitoring:wait.bufferBusyShort'),
    LATCH: t('monitoring:wait.latchShort'),
    LIBCACHE: t('monitoring:lock.lib'),
    ROWCACHE: t('monitoring:lock.row'),
    CURSOR: t('monitoring:wait.cursorPinShort'),
});

// 락 종류 분류(2026-11-03 시험 피드백 — 「종류가 너무 많아 혼란스럽다」). 칩과 설명 창이 이 묶음으로 보인다.
// 코드는 백엔드 LOCK_TYPE_EXPR 이 주는 값(enq: XX 의 XX, 나머지는 자체 분류). 목록에 없는 enqueue 는 「기타 enqueue」
const LOCK_GROUPS = [
    { key: 'dml', types: ['TX', 'TM', 'UL'] },
    { key: 'space', types: ['HW', 'TS', 'ST', 'TT', 'TC', 'SQ', 'US'] },
    { key: 'system', types: ['CF', 'JQ', 'DL', 'CI', 'RO', 'KO', 'DX', 'PS'] },
    { key: 'memory', types: ['BUFFER', 'LATCH', 'LIBCACHE', 'ROWCACHE', 'CURSOR'] },
];
const LOCK_GROUP_KEYS = [...LOCK_GROUPS.map(g => g.key), 'other'];
const lockGroupOf = (type) => (LOCK_GROUPS.find(g => g.types.includes(type)) || { key: 'other' }).key;
const LOCK_INFO_TYPES = LOCK_GROUPS.flatMap(g => g.types);
/** 락 종류 한 줄 설명 — 이름 · 설명(알려진 것만, 모르면 null) */
const lockDescOf = (t, type) => (LOCK_INFO_TYPES.includes(type)
    ? `${type} — ${t(`monitoring:lockInfo.type.${type}.name`)}\n${t(`monitoring:lockInfo.type.${type}.desc`)}` : null);
// 락 대기의 그래프 값(2026-11-03) — 세션 수 · 합계 시간 · AAS. 세션 수만 서버에 따로 묻는다(measure=sessions)
const LOCK_SCALES = ['sessions', 'total', 'aas'];

// 락 종류 → wait class — 락/enqueue 추이 그래프 색상을 대기이벤트 추이(ashPanel/historyWaitPanel)와
// 같은 색상군(waitClassColors.js)으로 맞추기 위한 매핑. enqueue 계열은 대체로 Application/Configuration,
// 버퍼·래치·캐시류는 Concurrency 로 분류된다(Oracle V$EVENT_NAME.WAIT_CLASS 관례).
// buildWaitClassColors 는 "이벤트→클래스" 맵을 받는 함수이지만 "락종류→클래스" 도 같은 모양이라 그대로 재사용한다.
const LOCK_TYPE_CLASS = {
    TX: 'Application', TM: 'Application', UL: 'Application', DL: 'Application',
    HW: 'Configuration', TS: 'Configuration', TC: 'Configuration', ST: 'Configuration', CF: 'Configuration',
    JQ: 'Scheduler',
    BUFFER: 'Concurrency', LATCH: 'Concurrency', LIBCACHE: 'Concurrency',
    ROWCACHE: 'Concurrency', CURSOR: 'Concurrency',
};

// 락 종류 고정 색 — 행 락(TX)은 붉은색(락 대기의 관례 색), 테이블 락(TM)은 주황, 나머지는 서로 멀리
const LOCK_TYPE_COLORS = {
    TX: '#dc2626', TM: '#f97316', UL: '#a855f7', HW: '#0ea5e9', TS: '#0284c7', ST: '#0891b2', CF: '#64748b',
    JQ: '#16a34a', BUFFER: '#2563eb', LATCH: '#14b8a6', LIBCACHE: '#ca8a04', ROWCACHE: '#9333ea', CURSOR: '#db2777',
};

// 백엔드 DIMENSION_EXPR 의 NVL 값 — 아래 그래프에서 그리지 않는 계열(데이터 키라 번역하지 않는다)
const NON_DRAWN_DIM_KEYS = ['(no sql)', '(no module)'];

/* eslint-disable-next-line no-restricted-syntax -- 백엔드 dbmsPerfRouter 의 OTHERS_KEY. 차트 계열 키라 번역하면 깨진다 */
const OTHERS_KEY_STR = '기타';

// 위아래로 겹쳐 두는 두 그래프(대기이벤트 추이 / 메트릭 추이)는 **같은 시간 범위**를 본다.
// 그런데 좌측 축 폭이 다르면(한쪽만 축 제목이 있다) 플롯 영역이 어긋나 같은 시각이
// 두 그래프에서 다른 x 에 찍힌다 — 위아래로 눈을 옮기며 대조하는 화면에서 이건 치명적이다.
// 폭을 못 박아 양 끝을 맞춘다.
const PAIRED_AXIS_WIDTH = 58;

// 절대값(row) 을 그 행의 합계 대비 비중(%)으로 변환 — 상위N + 기타를 쌓았을 때
// 그래프 윗부분이 항상 100% 에서 수평이 되도록 한다(예: 30/20/15/5/기타30 = 100%).
// "상위 N개가 전체에서 차지하는 비율(단일 라인)" 이 아니라 "각 항목의 비중을 쌓은 100% 스택" 이 목표.
// ASH 거스리기의 **쌓는 순서**. yFields 의 앞이 그래프 맨 아래다.
//
// 순서를 고정하는 이유: 매 조회마다 상위 N 이 바뀜어 같은 범주가 위아래로 뛰면
// 눈이 매번 다시 적응해야 한다. ON CPU 를 바닥에 두는 것은 관례이기도 하고
// (Oracle 의 대기 분석은 "CPU 위에 무엇이 쌓혔나" 로 읽는다), I/O 를 그 다음에
// 모으면 「연산 vs 기다림」 경계가 한 줄로 보인다.
// 백엔드 DIMENSION_EXPR 의 NVL(event, ...) 기본값이다 — 데이터 키라 번역하지 않는다.
const ON_CPU_KEY = 'ON CPU';
// wait_class 가 User I/O 또는 System I/O 인 계열을 골라낸다.
const IO_CLASS_RE = /I\/O/i;

/**
 * 기타를 **빼고** ON CPU → I/O → 나머지 순서로 정렬한 계열 목록.
 *
 * 기타를 빼더라도 **비율은 전체 합계 기준으로 계산한다**(toPercentSeries 는 전체 목록을
 * 받는다). 그래서 쌓은 높이가 100 에 몸 미치는 만큼이 곰 「나머지」이고, 그 여백이
 * 정직한 표현이다. 남은 것만으로 다시 100% 를 만들면 상위 4개가 부하 전부라고 거짓말한다.
 */
function orderWaitSeries(series, classMap) {
    const rest = (series || []).filter(k => k !== OTHERS_KEY_STR);
    const cm = classMap || {};
    const isIo = (k) => IO_CLASS_RE.test(cm[k] || '');
    return [
        ...rest.filter(k => k === ON_CPU_KEY),
        ...rest.filter(k => k !== ON_CPU_KEY && isIo(k)),
        ...rest.filter(k => k !== ON_CPU_KEY && !isIo(k)),
    ];
}

/** 계열 값에 같은 수를 곱한다(AAS × 묶음 초 = 그 묶음의 대기 시간 합). null 은 null 그대로 — 0 으로 메우지 않는다 */
function scaleSeries(data, series, factor) {
    return (data || []).map(row => {
        const out = { ...row };
        (series || []).forEach(k => { if (row[k] != null && row[k] !== '') out[k] = Math.round(Number(row[k]) * factor * 1000) / 1000; });
        return out;
    });
}

function toPercentSeries(data, series) {
    if (!data || !data.length || !series || !series.length) return [];
    return data.map(row => {
        const total = series.reduce((sum, k) => sum + (row[k] || 0), 0);
        const out = { t: row.t };
        if (total <= 0) {
            series.forEach(k => { out[k] = 0; });
            return out;
        }
        // **합이 정확히 100 이어야 한다.** 각자 반올림하면 0.1 씩 남아 합이 100.1 이 되고,
        // 그러면 축이 늘어나 "100% 스택인데 y축 최댓값이 105" 가 된다(실제 신고).
        // 최대잉여법: 내림으로 나눈 뒤 남은 몫을 소수부가 큰 순서로 하나씩 돌려준다.
        const UNITS = 1000;                        // 0.1% 단위
        const exact = series.map(k => ((row[k] || 0) / total) * UNITS);
        const floors = exact.map(v => Math.floor(v));
        let left = UNITS - floors.reduce((a, v) => a + v, 0);
        const order = exact
            .map((v, i) => ({ i, frac: v - floors[i] }))
            .sort((a, b) => b.frac - a.frac);
        for (let n = 0; n < order.length && left > 0; n++, left--) floors[order[n].i] += 1;
        series.forEach((k, i) => { out[k] = floors[i] / 10; });
        return out;
    });
}

// **지표별 배수 스케일은 걷어냈다**(2026-09-21).
// 값이 큰 지표를 10의 거듭제곱으로 나눠 그리던 방식인데, 그러면 y축 눈금이 계열마다 다른
// 뜻이 된다 — 범례에 (×100) 이라고 적어 둬도 축 하나에 두 가지 잣대가 섞인 것은 그대로다.
// 지금은 같은 문제를 **축 쪽에서** 푼다: y축 최대값 드래그(수동)와 값이 없는 구간만 접는
// 끊긴 축(자동). 둘 다 값을 건드리지 않으므로 눈금은 끝까지 진짜 값이다.
// 규칙·검증: frontend/src/utils/chartYScale.js

// embedded=true 면 자기 껍데기(overlay/header/탭바/footer)를 그리지 않고 **본문만** 돌려준다.
// 상위 컨테이너(OraclePerfAnalysisModal)가 헤더·탭·연결선택을 소유하기 위한 모드다.
// 이 모드에서는 activeTab/selectedConnId 를 prop 으로 받아 컨테이너가 통제한다.
const DbmsPerfAnalysisModal = ({
    isOpen, onClose, connections = [], darkMode = false,
    embedded = false, activeTab: activeTabProp, selectedConnId: selectedConnIdProp,
    // 담는 창이 이 본문을 숨겨 둔 동안(다른 묶음의 탭을 보는 중) — 화면 상태는 그대로 두고 자동 갱신만 멈춘다(2026-10-22)
    paused = false,
    // 세션은 담는 창(OraclePerfAnalysisModal)의 것이다 — 두 탭이 같은 세션을 쓴다.
    // 혼자 뜰 때(embedded=false)는 없을 수 있어 폴백을 둔다: 세션 없이 보내면
    // 백엔드가 예전처럼 일회용 세션으로 답한다.
    monParams, monRun,
    onSelectTop,   // (dimension, value, windowMin) → 컨테이너가 실행 중인 쿼리 탭으로 넘긴다
    onRequestTab,  // (child) → 컨테이너가 그 탭으로 옮긴다(「매핑 편집」 → 설정 탭, 2026-10-16)
    onOpenSqlHistory, // (sqlId) → 컨테이너가 실행 이력 탭에서 그 SQL 을 연다(범례의 SQL ID, 2026-10-27)
    // 락 대기 탭의 소스(2026-11-03) — 'rt' 실시간 락 대기(GV$ACTIVE_SESSION_HISTORY) | 'awr' 락 대기 이력(DBA_HIST_ACTIVE_SESS_HISTORY)
    lockSrc = 'rt',
}) => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [activeTabState, setActiveTab] = useState('realtime');
    const activeTab = embedded ? activeTabProp : activeTabState; // 'realtime' | 'history'
    const [selectedConnIdState, setSelectedConnId] = useState(null);
    const selectedConnId = embedded ? selectedConnIdProp : selectedConnIdState;
    const [backendError, setBackendError] = useState(null);

    const oracleConns = useMemo(
        () => connections.filter(c => c.dbType === 'oracle' || !c.dbType),
        [connections]
    );
    // 예시 데이터는 **기본 꺼짐**이고 사용자가 켤 때만 쓴다(2026-10-03 — 모든 창 공통). 조회가 실패해도 예시로
    // 갈아타지 않는다 — 그러면 실제 DB 를 보는 줄 알고 예시 값을 읽게 된다. 실패는 오류 배너로 말한다.
    const [useDemo, setUseDemo] = useState(false);
    // 창을 열 때마다 예시는 꺼진 상태로 — 앞에서 켠 것이 남아 재현되지 않게(2026-10-03). 저장하지도 않는다
    useEffect(() => { if (isOpen) setUseDemo(false); }, [isOpen]);
    // 참조가 고정돼야 아래 useCallback 들의 의존성이 흔들리지 않는다
    const run = useCallback((fn) => (monRun ? monRun(fn) : Promise.resolve().then(fn)), [monRun]);
    // 데모일 때는 세션을 싣지 않는다 — 백엔드가 DB 에 가지 않는다.
    const target = useCallback((extra) => (useDemo
        ? { demo: 1, ...extra }
        : { ...(monParams || (selectedConnId ? { connectionId: selectedConnId } : {})), ...extra }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [useDemo, monParams, selectedConnId]);
    // 설정 탭은 예시와 상관없이 늘 연결한 DB 의 파라미터 목록을 고른다(예시 토글을 그 탭에 두지 않는다)
    const settingsTarget = useCallback((extra) => ({ ...(monParams || (selectedConnId ? { connectionId: selectedConnId } : {})), ...extra }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [monParams, selectedConnId]);

    useEffect(() => {
        if (embedded) return;   // 연결 선택은 컨테이너 소유
        if (isOpen && !selectedConnId && oracleConns.length > 0) {
            setSelectedConnId(oracleConns[0].id);
        }
    }, [embedded, isOpen, oracleConns, selectedConnId]);

    // ===== 메트릭 카탈로그 (실시간/이력 공통) =====
    const [catalog, setCatalog] = useState([]);
    // 'pdb' 면 지표가 **그 PDB 의 것**이다(서버가 PDB 용 뷰에서 읽었다) — 화면이 그렇게 적는다
    const [metricScope, setMetricScope] = useState(null);
    const unitOf = useCallback((name) => ((catalog.find(m => m.metricName === name) || {}).metricUnit || ''), [catalog]);

    // ===== 대기이벤트 ↔ 시스템 메트릭 매핑(사용자별 — utils/waitMetricMap · 서버 perfMetricMapStore) =====
    // 예전의 즐겨찾기(★ 5개)를 대신한다: 볼 지표는 「어느 대기를 보고 있나」 에 따라 달라진다.
    const [userMap, setUserMap] = useState({});
    // 「매핑 편집」 → 설정 탭으로(2026-10-16 — 예전의 창 대신). 무엇을 고치러 왔는지 넘긴다
    const [settingsFocus, setSettingsFocus] = useState(null);
    const goSettings = (section, key) => {
        setSettingsFocus({ section, key: key || '*', nonce: Date.now() });
        if (embedded && onRequestTab) onRequestTab('settings'); else setActiveTab('settings');
    };
    useEffect(() => {
        if (!isOpen || (activeTab !== 'realtime' && activeTab !== 'history')) return;
        let alive = true;
        axios.get(`${PERF_API}/metric-map`).then((res) => { if (alive) setUserMap((res.data && res.data.map) || {}); })
            .catch(() => { /* 못 읽으면 기본 매핑으로 돈다 — 화면을 막지 않는다 */ });
        return () => { alive = false; };
    }, [isOpen, activeTab]);

    const fetchCatalog = useCallback(async () => {
        try {
            const res = await run(() => axios.get(`${PERF_API}/sysmetric-catalog`, {
                params: target({}),
            }));
            const rows = res.data?.data || [];
            setCatalog(rows);
            setMetricScope(res.data?.scope || null);
            setBackendError(null);
        } catch (err) {
            // 실접속 실패(Oracle 미기동 등) 시 그래프가 통째로 비지 않도록 예시 데이터로 자동 전환.
            setBackendError(err.message);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run, target]);

    useEffect(() => { if (isOpen) fetchCatalog(); }, [isOpen, fetchCatalog]);

    // ===== 대기이벤트 설명 팝오버 =====
    const [showWaitInfo, setShowWaitInfo] = useState(false);

    // ===== 탭 A: 실시간 성능 — 대기이벤트에서 출발해 원인·상관 지표를 찾아 간다(2026-10-14 개편) =====
    //   위: 대기이벤트 추이(늘 이것)   →   영역을 눌러 대기 하나를 고른다
    //   아래: 시스템 메트릭(그 대기에 매핑된 지표의 리본에서 고른 것) | SQL | 세션 | 모듈(그 대기를 겪은 것만)
    // 두 그래프의 가로축은 **같은 범위**다 — 서버가 준 DB 의 지금(`rtNow`) 하나에서 나온다.
    const [windowMin, setWindowMin] = useState(30);   // 30·60·120 또는 'all'(ASH 에 남은 처음부터)
    // 그래프를 끌어 고른 구간(2026-10-16) — 있으면 windowMin 대신 이것. 서버가 실제로 쓴 구간은 rtSpan(가로축)
    const [rtRange, setRtRange] = useState(null);
    const [rtSpan, setRtSpan] = useState(null);
    const rtPeriod = useMemo(() => (rtRange ? { fromT: rtRange.fromT, toT: rtRange.toT } : { windowMin }), [rtRange, windowMin]);
    // 「실행 중인 쿼리」 로 넘길 때의 창 — 그 탭은 최근 N분(10·30·60·120)만 받는다. 전체·고른 구간이면 가장 넓은 것
    const runningWindowMin = !rtRange && typeof windowMin === 'number' ? windowMin : 120;
    const [waitClass, setWaitClass] = useState('');
    const [ashData, setAshData] = useState([]);
    const [ashSeries, setAshSeries] = useState([]);
    // 백엔드가 여전히 shareKey(단일 "상위 비중" 라인)를 함께 주지만, 100% 스택 방식으로
    // 바꾼 뒤로는 쓰지 않는다 — 프론트에서 toPercentSeries() 로 직접 계산한다.
    const [ashWaitClass, setAshWaitClass] = useState(null);
    const [ashLoading, setAshLoading] = useState(false);
    const [rtNow, setRtNow] = useState(null);                  // DB 서버의 지금(시간대 없는 글)
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [pickedEvent, setPickedEvent] = useState(null);      // 위 그래프에서 고른 대기이벤트
    const [lowerMode, setLowerMode] = useState('metric');      // 'metric' | 'sql' | 'session' | 'module'
    // 「쿼리 보기」(2026-10-27) — 칸마다 그 조회가 **실제로 실행한** 문장(서버 응답의 sourceSql, monitorSession.attachSourceSql). 사본을 두지 않는다
    const [srcSql, setSrcSql] = useState({});
    const noteSrc = useCallback((slot, res) => {
        const list = res && res.data && res.data.sourceSql;
        setSrcSql(prev => ({ ...prev, [slot]: Array.isArray(list) ? list : [] }));
    }, []);
    const [qvSlot, setQvSlot] = useState(null);
    // 범례 설명 켜기(2026-10-27) — 위(대기이벤트) · 아래(시스템 메트릭·SQL·세션·모듈)
    const [hintUpper, setHintUpper] = useState(() => readHintPref('upper'));
    const [hintLower, setHintLower] = useState(() => readHintPref('lower'));
    // 범례의 SQL ID 설명에 붙일 SQL 앞부분(공유 풀) — 설명을 켰을 때만 받는다
    const [sqlHeads, setSqlHeads] = useState({});
    const [shownMetrics, setShownMetrics] = useState([]);      // 아래 그래프에 그릴 메트릭(고른 순서)
    const [sysmetricData, setSysmetricData] = useState([]);
    // 대기이벤트 그래프를 무엇으로 읽나(2026-10-14 요청) — 'pct' 비중(100% 스택) / 'time' 시간(받은 값 그대로).
    // 시간은 실시간이 평균 활성 세션(AAS — 1 이면 그 구간 내내 세션 하나가 그 대기를 겪었다), 이력이 스냅샷 구간의 대기 시간(초)이다
    const [rtScale, setRtScale] = useState('pct');
    // 노드 고르기(RAC, 2026-11-03 시험 피드백) — 비어 있으면 모든 노드. 실시간·이력 그래프가 함께 쓴다
    const [instPick, setInstPick] = useState([]);
    // 노드 그래프의 세로축 최대값(끌어서 정한 것, 2026-11-03) — 한 노드에서 끌면 같은 묶음의 다른 노드도 같은 값.
    // 묶음마다 { ctx, l, r } — 보는 방식(ctx: 눈금·고른 지표)이 바뀌면 그 값은 버린다(비중에서 정한 값이 합계 시간에 남으면 안 된다)
    const [yMaxes, setYMaxes] = useState({});
    // 대기 검색(2026-10-16) — 비어 있으면 상위 4개, 글이 있으면 그 글이 들어간 대기 전부(서버가 거른다)
    const [ashSearch, setAshSearch] = useState('');
    const [histSearch, setHistSearch] = useState('');
    // 그래프에서 대기를 눌러 검색 칸에 넣은 것이면 이름이 **정확히 같은** 대기만(exact) — 직접 친 글은 글자가 들어간 대기 전부(2026-10-16)
    const [ashExact, setAshExact] = useState(false);
    const [histExact, setHistExact] = useState(false);
    // 대기를 눌러 하나로 좁혔을 때 **누르기 전의 대기 목록**(범례에 흐리게 남겨 다시 고를 수 있게 — 2026-10-16). { keys, cls }
    const [ashBase, setAshBase] = useState(null);
    const [histBase, setHistBase] = useState(null);
    // 여러 대기를 골라 보기(2026-10-22 — 「기타」 를 눌러 묶인 대기를 풀어 본다). 고른 것이 있으면 검색 대신 그것만 계열로 그린다(서버 events)
    const [ashMulti, setAshMulti] = useState([]);
    const [histMulti, setHistMulti] = useState([]);
    const [ashOthers, setAshOthers] = useState([]);     // 「기타」 에 묶인 대기 — 서버가 준 목록 [{event, share, waitClass}]
    const [histOthers, setHistOthers] = useState([]);
    const [othersOpen, setOthersOpen] = useState(null);  // 'rt' | 'hist' | null — 고르기 상자가 열린 그래프
    const typeAshSearch = useCallback((v, opt) => { setAshSearch(v); setAshExact(!!(opt && opt.exact)); setAshBase(null); setAshMulti([]); }, []);
    const typeHistSearch = useCallback((v, opt) => { setHistSearch(v); setHistExact(!!(opt && opt.exact)); setHistBase(null); setHistMulti([]); }, []);
    const [histScale, setHistScale] = useState('pct');
    const [lowerLoading, setLowerLoading] = useState(false);
    const [relSettings, setRelSettings] = useState(null);       // { group, items, notes, isDemo }
    const [dimData, setDimData] = useState([]);                // SQL·세션·모듈 추이
    // 실시간 ASH 묶음 길이(초) — 「합계 시간」 = AAS × 묶음 길이(2026-10-16)
    const [rtBucketSec, setRtBucketSec] = useState(null);
    // 대기 수(2026-10-16, 보조 축 점선) — 이력에서 대기 하나를 골랐을 때만. 서버가 계열마다 `${prefix}<대기>` 칸으로 준다
    const [histWaitsPrefix, setHistWaitsPrefix] = useState(null);
    // 성능 이력의 1회당 평균 대기 시간(ms) — 서버가 같은 계열로 함께 준다
    const [historyWaitAvgData, setHistoryWaitAvgData] = useState([]);
    const [dimSeries, setDimSeries] = useState([]);

    // 고른 대기에 걸린 메트릭(이 DB 의 목록에 있는 것만 칩으로 선다 — 없는 것은 수로 말한다)
    const pickedClass = pickedEvent && ashWaitClass ? ashWaitClass[pickedEvent] : null;
    const mapping = useMemo(() => resolveMetrics(pickedEvent, pickedClass, userMap), [pickedEvent, pickedClass, userMap]);
    const candidates = useMemo(() => {
        const have = new Set(catalog.map(m => m.metricName));
        return { list: mapping.metrics.filter(m => have.has(m)), missing: mapping.metrics.filter(m => !have.has(m)).length };
    }, [mapping, catalog]);
    const candidateKey = candidates.list.join('\u0001');
    // 대기를 바꾸면 리본이 바뀐다 — 그 리본의 **첫 지표**를 골라 둔다(빈 그래프로 두지 않는다). 그 뒤는 사용자가 칩으로 고른다
    useEffect(() => {
        setShownMetrics(candidates.list.slice(0, 1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateKey]);
    const toggleShown = (name) => setShownMetrics(prev => (prev.includes(name) ? prev.filter(m => m !== name) : (canShow(prev, name, unitOf) ? [...prev, name] : prev)));
    const axes = useMemo(() => splitByUnit(shownMetrics, unitOf), [shownMetrics, unitOf]);

    // 노드별 데이터 — 인스턴스가 여럿이면 그래프를 노드마다 그린다(2026-10-14). 하나뿐이면 빈 배열(합계 그래프 하나)
    // 가장 나중 요청의 응답만 반영한다(2026-10-22) — 「전체 기간」 처럼 오래 걸리는 조회가 끝나기 전에 기간을 바꾸면, 늦게 온 옛 응답이
    // 새 응답을 덮어 선택 상자는 「최근 3일」 인데 그래프는 전체 기간으로 남았다(신고). 조회마다 번호를 올리고, 응답이 왔을 때 번호가 그대로인 것만 쓴다
    // 새 조회를 시작하면 같은 그래프의 **앞 요청을 취소**한다 — 큰 DB 에서 오래 걸리는 조회가 줄(run)을 막아, 기간을 골라도 그래프가 더는
    // 안 바뀌는 것처럼 보였다(신고). 서버는 끊긴 요청의 문장을 취소한다(monitorSession.cancelOnDisconnect)
    const reqSeqRef = useRef({});
    const reqCtlRef = useRef({});
    const beginReq = useCallback((key) => {
        const n = (reqSeqRef.current[key] || 0) + 1;
        reqSeqRef.current[key] = n;
        if (reqCtlRef.current[key]) reqCtlRef.current[key].abort();
        const ctl = new AbortController();
        reqCtlRef.current[key] = ctl;
        const current = () => reqSeqRef.current[key] === n;
        current.signal = ctl.signal;
        return current;
    }, []);
    // 창이 닫히면 진행 중인 조회를 모두 거둔다
    useEffect(() => () => { Object.values(reqCtlRef.current).forEach(c => c.abort()); }, []);
    // 줄(run — 세션 하나에 차례로)에서 기다리던 요청이 차례가 왔을 때 이미 지난 것이면 보내지 않는다 — 기간을 여러 번 바꿔도 마지막 것만 조회한다
    const runIf = useCallback((current, thunk) => run(() => (current() ? thunk() : Promise.resolve(null))), [run]);

    const [ashByInst, setAshByInst] = useState([]);
    const [dimByInst, setDimByInst] = useState([]);
    const [sysByInst, setSysByInst] = useState([]);
    const fetchAshTrend = useCallback(async () => {
        const current = beginReq('ashTrend');
        setAshLoading(true);
        try {
            const res = await runIf(current, () => axios.get(`${PERF_API}/ash-trend`, {
                signal: current.signal,
                params: target({
                    ...rtPeriod, dimension: 'wait_event', waitClass: waitClass || undefined, byInst: 1,
                    ...(ashMulti.length ? { events: JSON.stringify(ashMulti) }
                        : { search: ashSearch || undefined, exact: ashSearch && ashExact ? 1 : undefined }),
                }),
            }));
            if (!current()) return;
            noteSrc('rtWait', res);
            setAshData(res.data?.data || []);
            setAshOthers(res.data?.othersList || []);
            setAshByInst(res.data?.byInst || []);
            setRtBucketSec(Number(res.data?.bucketSec) || null);
            setAshSeries(res.data?.seriesKeys || []);
            setAshWaitClass(res.data?.waitClassMap || null);
            setRtNow(res.data?.dbNow || null);
            setRtSpan(res.data?.range || null);
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setAshLoading(false);
        }
    }, [beginReq, runIf, target, rtPeriod, waitClass, ashSearch, ashExact, ashMulti, noteSrc]);

    const shownKey = shownMetrics.join(',');
    const fetchLower = useCallback(async () => {
        const current = beginReq('lower');
        setLowerLoading(true);
        try {
            if (lowerMode === 'settings') {
                // 대기 이름과 그 대기의 그룹만 보낸다 — 읽을 파라미터는 서버의 표가 정한다
                const res = await runIf(current, () => axios.get(`${PERF_API}/related-settings`, {
                    signal: current.signal,
                    params: target({ event: pickedEvent || '', waitClass: (pickedEvent && ashWaitClass && ashWaitClass[pickedEvent]) || waitClass || '' }),
                }));
                if (!current()) return;
                noteSrc('rtLower', res);
                setRelSettings(res.data || null);
            } else if (lowerMode === 'metric') {
                if (!shownKey) { setSysmetricData([]); return; }
                const res = await runIf(current, () => axios.get(`${PERF_API}/sysmetric`, { signal: current.signal, params: target({ ...rtPeriod, metricNames: shownKey, byInst: 1 }) }));
                if (!current()) return;
                noteSrc('rtLower', res);
                setSysmetricData(res.data?.data || []);
                setSysByInst(res.data?.byInst || []);
            } else {
                // 고른 대기가 있으면 **그 대기를 겪은 표본만** 센다 — 「이 대기는 어느 SQL·세션·모듈의 것인가」
                const res = await runIf(current, () => axios.get(`${PERF_API}/ash-trend`, {
                    signal: current.signal,
                    params: target({ ...rtPeriod, dimension: lowerMode, event: pickedEvent || undefined, byInst: 1 }),
                }));
                if (!current()) return;
                noteSrc('rtLower', res);
                setDimData(res.data?.data || []);
                setDimByInst(res.data?.byInst || []);
                setDimSeries(res.data?.seriesKeys || []);
            }
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setLowerLoading(false);
        }
    }, [beginReq, runIf, target, rtPeriod, lowerMode, shownKey, pickedEvent, ashWaitClass, waitClass, noteSrc]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'realtime') return;
        fetchAshTrend();
    }, [isOpen, activeTab, fetchAshTrend]);
    useEffect(() => {
        if (!isOpen || activeTab !== 'realtime') return;
        fetchLower();
    }, [isOpen, activeTab, fetchLower]);

    // 위아래를 **함께** 다시 받는다 — 한쪽만 새로 받으면 두 그래프의 끝이 어긋난다
    const refreshRealtime = useCallback(() => { fetchAshTrend(); fetchLower(); }, [fetchAshTrend, fetchLower]);
    useEffect(() => {
        if (!isOpen || paused || activeTab !== 'realtime' || !autoRefresh) return undefined;
        const timer = setInterval(refreshRealtime, 30000);
        return () => clearInterval(timer);
    }, [isOpen, paused, activeTab, autoRefresh, refreshRealtime]);
    // 연결·예시를 바꾸면 고른 대기는 다른 DB 의 것이다
    useEffect(() => { setPickedEvent(null); }, [useDemo, selectedConnId]);
    // 연결·예시를 바꾸면 끌어 고른 구간은 버린다(다른 DB 의 시각이다)
    useEffect(() => { setRtRange(null); setHistRange(null); setAshBase(null); setHistBase(null); }, [useDemo, selectedConnId]);
    const selectRtRange = useCallback((a, b) => { if (b - a >= MIN_DRAG_MS.rt) setRtRange({ fromT: wallText(a), toT: wallText(b) }); }, []);
    const selectHistRange = useCallback((a, b) => { if (b - a >= MIN_DRAG_MS.hist) setHistRange({ fromT: wallText(a), toT: wallText(b) }); }, []);

    // ===== 탭 B: 성능 이력 =====
    const [historyDays, setHistoryDays] = useState(7);   // 1·3·7·14 또는 'all'(AWR 스냅샷의 처음부터)
    const [histRange, setHistRange] = useState(null);       // 이력 그래프를 끌어 고른 구간
    const [histSpan, setHistSpan] = useState(null);
    const histPeriod = useMemo(() => (histRange ? { fromT: histRange.fromT, toT: histRange.toT } : { days: historyDays }), [histRange, historyDays]);
    // 대기 그룹별 대기 이름(검색 후보) — 그룹마다 한 번 받는다. 연결·예시가 바뀌면 다시
    const [waitNames, setWaitNames] = useState({});
    useEffect(() => { setWaitNames({}); }, [useDemo, selectedConnId]);
    const [historyWaitClass, setHistoryWaitClass] = useState('');
    const [historyMetricData, setHistoryMetricData] = useState([]);
    const [historyMetricLoading, setHistoryMetricLoading] = useState(false);
    const [historyWaitData, setHistoryWaitData] = useState([]);
    const [historyWaitSeries, setHistoryWaitSeries] = useState([]);
    const [historyWaitClassMap, setHistoryWaitClassMap] = useState(null);
    // 고른 대기 그룹의 대기 이름을 받아 둔다(실시간·이력 각각의 그룹). 실패하면 비워 두고 아는 것으로 거른다(suggestionsFor)
    useEffect(() => {
        if (!isOpen) return undefined;
        let alive = true;
        [waitClass, historyWaitClass].filter(Boolean).forEach((wc) => {
            if (waitNames[wc]) return;
            run(() => axios.get(`${PERF_API}/wait-event-names`, { params: target({ waitClass: wc }) }))
                .then((r) => { if (alive) setWaitNames(m => ({ ...m, [wc]: ((r.data && r.data.data) || []).map(x => x.name) })); })
                .catch(() => { /* 아는 것으로 거른다 */ });
        });
        return () => { alive = false; };
    }, [isOpen, waitClass, historyWaitClass, run, target]); // eslint-disable-line react-hooks/exhaustive-deps
    const [historyWaitLoading, setHistoryWaitLoading] = useState(false);
    const [histNow, setHistNow] = useState(null);                 // DB 서버의 지금 — 두 이력 그래프의 가로축 범위
    const [histPicked, setHistPicked] = useState(null);           // 이력의 대기이벤트 그래프에서 고른 대기
    const [histShown, setHistShown] = useState([]);
    const histClass = histPicked && historyWaitClassMap ? historyWaitClassMap[histPicked] : null;
    const histMapping = useMemo(() => resolveMetrics(histPicked, histClass, userMap), [histPicked, histClass, userMap]);
    const histCandidates = useMemo(() => {
        const have = new Set(catalog.map(m => m.metricName));
        return { list: histMapping.metrics.filter(m => have.has(m)), missing: histMapping.metrics.filter(m => !have.has(m)).length };
    }, [histMapping, catalog]);
    const histCandidateKey = histCandidates.list.join('\u0001');
    useEffect(() => {
        setHistShown(histCandidates.list.slice(0, 1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [histCandidateKey]);
    const toggleHistShown = (name) => setHistShown(prev => (prev.includes(name) ? prev.filter(m => m !== name) : (canShow(prev, name, unitOf) ? [...prev, name] : prev)));
    const histAxes = useMemo(() => splitByUnit(histShown, unitOf), [histShown, unitOf]);
    const histShownKey = histShown.join(',');

    const [histWaitByInst, setHistWaitByInst] = useState([]);
    const [histMetricByInst, setHistMetricByInst] = useState([]);
    const fetchHistoryMetric = useCallback(async () => {
        const current = beginReq('histMetric');
        if (!histShownKey) { setHistoryMetricData([]); return; }
        setHistoryMetricLoading(true);
        try {
            const res = await runIf(current, () => axios.get(`${PERF_API}/history-sysmetric`, {
                signal: current.signal,
                params: target({ ...histPeriod, metricNames: histShownKey, byInst: 1 }),
            }));
            if (!current()) return;
            noteSrc('histMetric', res);
            setHistoryMetricData(res.data?.data || []);
            setHistMetricByInst(res.data?.byInst || []);
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setHistoryMetricLoading(false);
        }
    }, [beginReq, runIf, target, histPeriod, histShownKey, noteSrc]);

    const fetchHistoryWait = useCallback(async () => {
        const current = beginReq('histWait');
        setHistoryWaitLoading(true);
        try {
            const res = await runIf(current, () => axios.get(`${PERF_API}/history-wait-events`, {
                signal: current.signal,
                params: target({
...histPeriod, waitClass: historyWaitClass || undefined, byInst: 1,
                    ...(histMulti.length ? { events: JSON.stringify(histMulti) }
                        : { search: histSearch || undefined, exact: histSearch && histExact ? 1 : undefined }),
                }),
            }));
            if (!current()) return;
            noteSrc('histWait', res);
            setHistoryWaitData(res.data?.data || []);
            setHistOthers(res.data?.othersList || []);
            setHistoryWaitAvgData(res.data?.avgData || []);
            setHistWaitsPrefix(res.data?.waitsPrefix || null);
            setHistWaitByInst(res.data?.byInst || []);
            setHistoryWaitSeries(res.data?.seriesKeys || []);
            setHistoryWaitClassMap(res.data?.waitClassMap || null);
            setHistNow(res.data?.dbNow || null);
            setHistSpan(res.data?.range || null);
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setHistoryWaitLoading(false);
        }
    }, [beginReq, runIf, target, histPeriod, historyWaitClass, histSearch, histExact, histMulti, noteSrc]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'history') return;
        fetchHistoryMetric();
    }, [isOpen, activeTab, fetchHistoryMetric]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'history') return;
        fetchHistoryWait();
    }, [isOpen, activeTab, fetchHistoryWait]);

    // 성능 이력의 아래 그래프(2026-10-18) — 실시간처럼 시스템 메트릭 · SQL · 세션 · 모듈 · 관련 설정. SQL·세션·모듈은 AWR 에 남은 ASH(서버 /history-ash-trend)
    const [histLowerMode, setHistLowerMode] = useState('metric');
    const [histDimData, setHistDimData] = useState([]);
    const [histDimByInst, setHistDimByInst] = useState([]);
    const [histDimSeries, setHistDimSeries] = useState([]);
    const [histDimBucketSec, setHistDimBucketSec] = useState(null);
    const [histRelSettings, setHistRelSettings] = useState(null);
    const [histLowerLoading, setHistLowerLoading] = useState(false);
    const fetchHistLower = useCallback(async () => {
        const current = beginReq('histLower');
        if (histLowerMode === 'metric') return;   // 메트릭은 fetchHistoryMetric
        setHistLowerLoading(true);
        try {
            if (histLowerMode === 'settings') {
                const res = await runIf(current, () => axios.get(`${PERF_API}/related-settings`, {
                    // hist=1 — 그 구간 **당시** 값(AWR 의 DBA_HIST_PARAMETER · DBA_HIST_LOG)
                    signal: current.signal,
                    params: target({ ...histPeriod, hist: 1, event: histPicked || '', waitClass: (histPicked && historyWaitClassMap && historyWaitClassMap[histPicked]) || historyWaitClass || '' }),
                }));
                if (!current()) return;
                noteSrc('histLower', res);
                setHistRelSettings(res.data || null);
            } else {
                // 고른 대기가 있으면 그 대기를 겪은 표본만 — 「이 대기는 어느 SQL·세션·모듈의 것이었나」
                const res = await runIf(current, () => axios.get(`${PERF_API}/history-ash-trend`, {
                    signal: current.signal,
                    params: target({ ...histPeriod, dimension: histLowerMode, event: histPicked || undefined, byInst: 1 }),
                }));
                if (!current()) return;
                noteSrc('histLower', res);
                setHistDimData(res.data?.data || []);
                setHistDimByInst(res.data?.byInst || []);
                setHistDimSeries(res.data?.seriesKeys || []);
                setHistDimBucketSec(Number(res.data?.bucketSec) || null);
            }
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setHistLowerLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beginReq, runIf, target, histPeriod, histLowerMode, histPicked, historyWaitClass]);
    useEffect(() => {
        if (!isOpen || activeTab !== 'history') return;
        fetchHistLower();
    }, [isOpen, activeTab, fetchHistLower]);

    // ===== 탭 C: 락 대기 (ASH blocking_session 체인) =====
    const [lockWindowMin, setLockWindowMin] = useState(30);
    const [lockTree, setLockTree] = useState([]);
    const [lockLoading, setLockLoading] = useState(false);
    const [lockAutoRefresh, setLockAutoRefresh] = useState(false);
    // 락 종류 — **서버가 거른다**(그래프·트리 모두). 기본 TX·TM, 'ALL' 이면 전부(2026-10-14)
    const [lockTypeFilter, setLockTypeFilter] = useState(DEFAULT_LOCK_TYPES);
    const [lockTypeCounts, setLockTypeCounts] = useState([]);   // [{type, sessions, samples}] — 구간 안에 나타난 종류
    // 그래프에서 끌어 고른 구간 { fromMs, toMs } — 있으면 트리가 그 구간만 본다(그래프는 창 전체를 그대로 보이고 음영으로 표시)
    const [lockRange, setLockRange] = useState(null);
    const [lockMeta, setLockMeta] = useState({ truncated: false });
    const [lockTrendByInst, setLockTrendByInst] = useState([]);
    // 락/enqueue 추이 그래프 — 트리는 "지금 걸린 체인"만 보여주므로, 그 위에
    // "그 종류가 시간대별로 얼마나 자주/오래 나타났나" 를 같은 창(lockWindowMin)으로 겹쳐 본다.
    const [lockTrendData, setLockTrendData] = useState([]);
    const [lockTrendSeries, setLockTrendSeries] = useState([]);
    const [lockTrendLoading, setLockTrendLoading] = useState(false);
    const [lockNow, setLockNow] = useState(null);   // DB 서버의 지금 — 추이 그래프의 가로축 범위
    // 락 대기 이력(AWR) — 기간은 일 단위(성능 이력과 같은 선택지), 가로축은 서버가 실제로 쓴 구간(lockSpan)
    const isLockHist = lockSrc === 'awr';
    const [lockDays, setLockDays] = useState(1);
    const [lockSpan, setLockSpan] = useState(null);
    // 그래프 값 — 세션 수 · 합계 시간 · AAS(실시간 성능의 비중·합계 시간·AAS 와 같은 방식)
    const [lockScale, setLockScale] = useState('aas');
    const lockMeasure = lockScale === 'sessions' ? 'sessions' : 'aas';
    const [lockBucketSec, setLockBucketSec] = useState(null);
    const [showLockInfo, setShowLockInfo] = useState(false);
    // 트리에서 고른 SQL — 오른쪽 칸에 원문을 보인다(2026-10-07). { sqlId, text, loading, error }
    // 트리 노드에 이미 원문(gv$sql)이 실려 오면 그것을 쓰고, 공유 풀에서 밀려 비었으면 원문 라우트로 다시 묻는다.
    const [lockSql, setLockSql] = useState(null);
    const openLockSql = useCallback(async (node) => {
        if (!node || !node.sqlId) return;
        if (node.sqlText) { setLockSql({ sqlId: node.sqlId, text: node.sqlText, source: node.sqlSource || null }); return; }
        setLockSql({ sqlId: node.sqlId, loading: true });
        try {
            const res = await run(() => axios.get(SQL_TEXT_API, { params: target({ sqlId: node.sqlId }) }));
            setLockSql(v => (v && v.sqlId === node.sqlId ? { sqlId: node.sqlId, text: res.data?.sqlText ?? null, source: res.data?.source || null } : v));
        } catch (err) {
            setLockSql(v => (v && v.sqlId === node.sqlId ? { sqlId: node.sqlId, error: formatApiError(err, t) } : v));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run, target]);
    // 예시 ↔ 실제를 바꾸면 보던 원문은 다른 DB 의 것이 된다 — 비운다
    useEffect(() => { setLockSql(null); }, [useDemo, selectedConnId]);

    const fetchLockTree = useCallback(async () => {
        const current = beginReq('lockTree');
        setLockLoading(true);
        try {
            const res = await runIf(current, () => axios.get(`${PERF_API}/lock-tree`, {
                signal: current.signal,
                params: target({
                    src: lockSrc,
                    ...(isLockHist ? { days: lockDays } : { windowMin: lockWindowMin }),
                    lockTypes: lockTypeFilter === 'ALL' ? 'ALL' : lockTypeFilter.join(','),
                    ...(lockRange ? { fromT: wallText(lockRange.fromMs), toT: wallText(lockRange.toMs) } : {}),
                }),
            }));
            if (!current()) return;
            noteSrc('lockTree', res);
            setLockTree(res.data?.tree || []);
            setLockTypeCounts(res.data?.typeCounts || []);
            setLockMeta({ truncated: !!res.data?.truncated, sampleSec: res.data?.sampleSec || 1, range: res.data?.range || null });
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setLockLoading(false);
        }
    }, [beginReq, runIf, target, lockSrc, isLockHist, lockDays, lockWindowMin, lockTypeFilter, lockRange, noteSrc]);

    const fetchLockTrend = useCallback(async () => {
        const current = beginReq('lockTrend');
        setLockTrendLoading(true);
        try {
            // 이력은 AWR 의 ASH(history-ash-trend — 10초 표본), 실시간은 GV$ASH(ash-trend). 같은 분류식 · 같은 락 종류 조건
            const res = await runIf(current, () => axios.get(`${PERF_API}/${isLockHist ? 'history-ash-trend' : 'ash-trend'}`, {
                signal: current.signal,
                params: target({
                    ...(isLockHist ? { days: lockDays } : { windowMin: lockWindowMin }),
                    dimension: 'lock_type', byInst: 1, measure: lockMeasure,
                    lockTypes: lockTypeFilter === 'ALL' ? 'ALL' : lockTypeFilter.join(','),
                }),
            }));
            if (!current()) return;
            noteSrc('lockTrend', res);
            setLockTrendData(res.data?.data || []);
            setLockTrendByInst(res.data?.byInst || []);
            setLockTrendSeries(res.data?.seriesKeys || []);
            setLockNow(res.data?.dbNow || null);
            setLockSpan(res.data?.range || null);
            setLockBucketSec(res.data?.bucketSec || null);
            setBackendError(null);
        } catch (err) {
            if (current()) setBackendError(err.message);
        } finally {
            if (current()) setLockTrendLoading(false);
        }
    }, [beginReq, runIf, target, isLockHist, lockDays, lockWindowMin, lockMeasure, lockTypeFilter, noteSrc]);
    // 창(30분·1시간·2시간 / 이력은 일)을 바꾸면 끌어 고른 구간은 버린다 — 새 창 밖일 수 있다
    useEffect(() => { setLockRange(null); }, [lockWindowMin, lockDays, useDemo, selectedConnId]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'lockwait') return;
        fetchLockTree();
        fetchLockTrend();
    }, [isOpen, activeTab, fetchLockTree, fetchLockTrend]);

    useEffect(() => {
        if (!isOpen || paused || activeTab !== 'lockwait' || !lockAutoRefresh || isLockHist) return undefined;
        const timer = setInterval(() => { fetchLockTree(); fetchLockTrend(); }, 30000);
        return () => clearInterval(timer);
    }, [isOpen, paused, activeTab, lockAutoRefresh, isLockHist, fetchLockTree, fetchLockTrend]);

    // 트리를 펼쳐서 센 총 노드 수 — "차단 체인 N건 · 관련 세션 M개" 요약에 쓴다.
    const lockNodeCount = useMemo(() => {
        let count = 0;
        const walk = (nodes) => { nodes.forEach(n => { count++; walk(n.children || []); }); };
        walk(lockTree);
        return count;
    }, [lockTree]);

    // 고를 수 있는 락 종류 — **이 구간에 실제로 나타난 종류만**(2026-11-03 시험 피드백, 기다린 세션 수와 함께). 서버의 typeCounts 는 거르기와 무관한 전체다.
    // 고른 것 중 이 구간에 없는 것은 칩으로 세우지 않고 한 줄로 말한다(lockTypeAbsent) — 무엇이 걸려 있는지는 보여야 한다
    const lockTypeCatalog = useMemo(() => lockTypeCounts
        .filter(c => (c.sessions || 0) > 0)
        .map(c => ({ type: c.type, sessions: c.sessions || 0, group: lockGroupOf(c.type) }))
        .sort((a, b) => (b.sessions - a.sessions) || a.type.localeCompare(b.type)), [lockTypeCounts]);
    const lockTypeGroups = LOCK_GROUP_KEYS
        .map(key => ({ key, items: lockTypeCatalog.filter(c => c.group === key) }))
        .filter(g => g.items.length > 0);
    const lockTypeAbsent = lockTypeFilter === 'ALL' ? [] : lockTypeFilter.filter(type => !lockTypeCatalog.some(c => c.type === type));
    const lockTypeOn = (type) => lockTypeFilter === 'ALL' || lockTypeFilter.includes(type);
    const toggleLockTypeFilter = (type) => {
        setLockTypeFilter(prev => {
            const cur = prev === 'ALL' ? lockTypeCatalog.map(c => c.type) : prev;
            const next = cur.includes(type) ? cur.filter(x => x !== type) : [...cur, type];
            return next.length === 0 ? DEFAULT_LOCK_TYPES : next;
        });
    };
    // 서버가 고른 종류만 준다 — 화면에서 다시 거르지 않는다
    const filteredLockTree = lockTree;

    // 드릴다운 불가 계열 — "기타" 묶음은 특정 sql/session/module 을 가리키지 않고,
    // '(no sql)' 은 정의상 sql_id 가 없는 샘플 묶음이라 눌러도 반드시 빈 결과가 된다.
    const isNonDrillableSeries = (key) => key === OTHERS_KEY_STR || key === '(no sql)';

    // 그래프 영역(Area) 이나 범례를 클릭하면 그 계열로 바로 드릴다운한다 —
    // 예전에는 차트 아래 별도 목록에서 골라야 했는데, 그래프 안에서 이미 상위4+기타로
    // 구분돼 보이는 범주와 중복이라 목록은 없애고 그래프 자체를 클릭 지점으로 삼는다.
    // 위 그래프(대기이벤트)의 영역·범례를 누르면 **그 대기를 고른다** — 아래에 그 대기에 매핑된 지표의 리본이 서고,
    // 아래가 SQL·세션·모듈이면 그 대기를 겪은 것만 다시 센다. 같은 것을 다시 누르면 고른 것을 푼다.
    // 대기를 누르면 그 대기를 고르고 **대기 검색에 넣어 그 대기로 한정**한다(2026-10-16). 고른 대기를 다시 누르면 둘 다 풀린다
    // 범례에는 누르기 전의 대기가 그대로 남고 고른 것만 그린다 — 흐린 대기를 누르면 그것으로 바뀐다(토글)
    // 「기타」 를 누르면 묶인 대기를 골라 따로 보는 상자를 연다(2026-10-22 — 예전에는 아무 반응이 없었다).
    // 여러 대기를 골라 보는 중에는 대기를 눌러도 그 목록을 바꾸지 않는다 — 아래 그래프의 대상만 고른다(목록은 상자에서 고친다)
    const handleAshChartClick = (el) => {
        if (!el || !el.series) return;
        if (el.series === OTHERS_KEY_STR) { setOthersOpen(o => (o === 'rt' ? null : 'rt')); return; }
        if (ashMulti.length) { setPickedEvent(p => (p === el.series ? null : el.series)); return; }
        if (pickedEvent === el.series) { setPickedEvent(null); setAshSearch(''); setAshExact(false); setAshBase(null); return; }
        if (!ashBase) setAshBase({ keys: ashSeries.filter(k => k !== OTHERS_KEY_STR), cls: { ...(ashWaitClass || {}) } });
        setPickedEvent(el.series); setAshSearch(el.series); setAshExact(true);
    };
    const handleHistWaitClick = (el) => {
        if (!el || !el.series) return;
        if (el.series === OTHERS_KEY_STR) { setOthersOpen(o => (o === 'hist' ? null : 'hist')); return; }
        if (histMulti.length) { setHistPicked(p => (p === el.series ? null : el.series)); return; }
        if (histPicked === el.series) { setHistPicked(null); setHistSearch(''); setHistExact(false); setHistBase(null); return; }
        if (!histBase) setHistBase({ keys: historyWaitSeries.filter(k => k !== OTHERS_KEY_STR), cls: { ...(historyWaitClassMap || {}) } });
        setHistPicked(el.series); setHistSearch(el.series); setHistExact(true);
    };
    // 상자에서 고른 대기로 보기 — 검색·한 대기 고르기는 푼다(둘이 겹치면 무엇으로 좁혔는지 흐려진다). 아래 그래프의 대상은 고른 것 안에 있을 때만 남긴다
    const applyAshMulti = (names) => {
        setAshSearch(''); setAshExact(false); setAshBase(null); setAshMulti(names);
        setPickedEvent(p => (p && names.includes(p) ? p : null)); setOthersOpen(null);
    };
    const applyHistMulti = (names) => {
        setHistSearch(''); setHistExact(false); setHistBase(null); setHistMulti(names);
        setHistPicked(p => (p && names.includes(p) ? p : null)); setOthersOpen(null);
    };
    const resetAshMulti = () => { setAshMulti([]); setOthersOpen(null); };
    const resetHistMulti = () => { setHistMulti([]); setOthersOpen(null); };
    // 아래 그래프(SQL·세션·모듈)의 영역을 누르면 예전처럼 「실행 중인 쿼리」 로 넘어간다
    // 범례 SQL 설명용 앞부분 — 아래 그래프가 SQL 기준이고 설명을 켰을 때만, 아직 없는 것만(10개까지). 공유 풀에서 밀려났으면 빈 글
    const shownSqlIds = [...new Set([...(lowerMode === 'sql' ? dimSeries : []), ...(histLowerMode === 'sql' ? histDimSeries : [])])]
        .filter(k => SQL_ID_LIKE.test(k));
    const sqlIdsKey = (hintLower && !useDemo && isOpen) ? shownSqlIds.join(',') : '';
    useEffect(() => {
        if (!sqlIdsKey) return undefined;
        let alive = true;
        const ids = sqlIdsKey.split(',').filter(id => sqlHeads[id] === undefined).slice(0, 10);
        (async () => {
            for (const id of ids) {
                if (!alive) return;
                let head = '';
                try {
                    const res = await run(() => axios.get(SQL_TEXT_API, { params: target({ sqlId: id }) }));
                    head = String(res.data?.sqlText || '').replace(/\s+/g, ' ').trim().slice(0, 160);
                } catch (_e) { /* 설명이 없을 뿐이다 */ }
                if (alive) setSqlHeads(prev => ({ ...prev, [id]: head }));
            }
        })();
        return () => { alive = false; };
    }, [sqlIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps -- 보이는 SQL 이 바뀔 때만
    // 연결·예시를 바꾸면 받아 둔 글은 다른 DB 의 것이다
    useEffect(() => { setSqlHeads({}); setSrcSql({}); }, [selectedConnId, useDemo]);
    // 범례의 SQL ID 를 누르면 실행 이력 탭에서 그 SQL 을 연다 — 탭을 옮기므로 먼저 묻는다(2026-10-27 사용자 요청)
    const jumpToSqlHistory = async (sqlId) => {
        if (!onOpenSqlHistory || !SQL_ID_LIKE.test(String(sqlId || ''))) return;
        const ok = await confirm({
            title: t('monitoring:perf.toHist.title'),
            message: t('monitoring:perf.toHist.ask', { sqlId }),
            confirmText: t('monitoring:perf.toHist.ok'),
        });
        if (ok) onOpenSqlHistory(sqlId);
    };
    // 범례 클릭은 x·payload 가 없다(PanelChart handleLegendClick) — 영역 클릭과 가른다
    const isLegendClick = (el) => el && el.payload === null && el.x === undefined;
    const handleDimChartClick = (el) => {
        if (!el || !el.series || isNonDrillableSeries(el.series)) return;
        if (lowerMode === 'sql' && isLegendClick(el) && onOpenSqlHistory) { jumpToSqlHistory(el.series); return; }
        if (!onSelectTop) return;
        onSelectTop(lowerMode, el.series, runningWindowMin);
    };
    // 성능 이력의 아래 그래프 — 영역을 눌러 넘어갈 곳은 없고(실행 중인 쿼리는 지금의 것이다), SQL 범례만 실행 이력으로
    const handleHistDimClick = (el) => {
        if (!el || !el.series || isNonDrillableSeries(el.series)) return;
        if (histLowerMode === 'sql' && isLegendClick(el)) jumpToSqlHistory(el.series);
    };

    // ── 훅은 조기 return 위에 모아 둔다 ──────────────────────────────────────
    // 아래 `if (!isOpen) return null` 뒤에 useMemo 가 있으면 모달을 닫은 렌더와 연 렌더의
    // 훅 개수가 달라져 React 가 훅 상태를 잘못 매칭한다(rules-of-hooks 위반). 값이 뒤섞이거나
    // 크래시할 수 있어 가드보다 위로 올렸다. 의존 변수는 모두 이 지점 이전에 정의돼 있다.
    // 시간으로 볼 때는 받은 값 그대로(서버가 준 단위 — 실시간 AAS, 이력 초), 비중으로 볼 때만 100% 로 바꾼다
    // 실시간: 비중 = 100% / 합계 시간 = AAS × 묶음 길이(초) / 평균 시간 = AAS(받은 값 그대로).
    // 성능 이력: 비중 = 100% / 합계 시간 = 받은 초 / 평균 시간 = 서버가 준 1회당 평균(ms, 따로 받은 표)
    const shapeRt = (data, series) => (rtScale === 'pct' ? toPercentSeries(data, series)
        : rtScale === 'total' ? scaleSeries(data, series, rtBucketSec || 1) : data);
    const ashPctData = useMemo(() => shapeRt(ashData, ashSeries), [ashData, ashSeries, rtScale, rtBucketSec]); // eslint-disable-line react-hooks/exhaustive-deps
    const dimPctData = useMemo(() => shapeRt(dimData, dimSeries), [dimData, dimSeries, rtScale, rtBucketSec]); // eslint-disable-line react-hooks/exhaustive-deps
    // 지표 값은 손대지 않고 그대로 그린다 — 축 보정은 PanelChart 가 한다.
    const historyWaitPctData = useMemo(() => (histScale === 'pct' ? toPercentSeries(historyWaitData, historyWaitSeries)
        : histScale === 'avg' ? historyWaitAvgData : historyWaitData), [historyWaitData, historyWaitAvgData, historyWaitSeries, histScale]);
    // 노드가 여럿이면 노드마다 한 장 — 같은 계열·같은 가로축. 하나뿐이면 합계 한 장(inst: null). shape 를 주면 그것으로 바꾼다
    // 노드마다의 계열(keys)은 서버가 준 **그 노드의 상위 N**이다(2026-11-03 — 합계의 상위 N 을 모든 노드에 쓰면 노드별로 다른 대기가 가려졌다).
    // 고른 노드만(instPick) — 고른 것이 하나도 없으면(사라진 노드) 모두
    const perInst = (byInst, total, shape) => {
        if (!(Array.isArray(byInst) && byInst.length > 1)) return [{ inst: null, data: total, keys: null }];
        const shown = instPick.length ? byInst.filter(x => instPick.includes(x.inst)) : byInst;
        return (shown.length ? shown : byInst).map(x => ({ inst: x.inst, data: shape ? shape(x) : x.data, keys: Array.isArray(x.seriesKeys) ? x.seriesKeys : null }));
    };
    const ashCharts = useMemo(() => perInst(ashByInst, ashPctData, x => shapeRt(x.data, x.seriesKeys || ashSeries)), [ashByInst, ashPctData, ashSeries, rtScale, rtBucketSec, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    const dimCharts = useMemo(() => perInst(dimByInst, dimPctData, x => shapeRt(x.data, x.seriesKeys || dimSeries)), [dimByInst, dimPctData, dimSeries, rtScale, rtBucketSec, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    const sysCharts = useMemo(() => perInst(sysByInst, sysmetricData, null), [sysByInst, sysmetricData, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    const histWaitCharts = useMemo(() => perInst(histWaitByInst, historyWaitPctData, x => (histScale === 'pct' ? toPercentSeries(x.data, x.seriesKeys || historyWaitSeries)
        : histScale === 'avg' ? (x.avgData || []) : x.data)), [histWaitByInst, historyWaitPctData, historyWaitSeries, histScale, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    // 락 대기 추이 — 합계 시간은 AAS × 묶음 길이(초), 세션 수 · AAS 는 받은 그대로. 노드는 실시간 성능과 같은 고르기(instPick)
    const shapeLock = (data, series) => (lockScale === 'total' ? scaleSeries(data, series, lockBucketSec || 1) : data);
    const lockCharts = useMemo(() => perInst(lockTrendByInst, shapeLock(lockTrendData, lockTrendSeries), x => shapeLock(x.data, x.seriesKeys || lockTrendSeries)),
        [lockTrendByInst, lockTrendData, lockTrendSeries, lockScale, lockBucketSec, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    // 비중 / 시간 고르기 — 그래프 제목 줄 오른쪽
    // 검색 상자의 후보 — 지금 보이는 대기 + 대기이벤트 설명표의 이름들
    const waitSuggestions = [...new Set([...ashSeries, ...historyWaitSeries, ...getWaitEventInfo(t).map(w => w.event)])].filter(e => e !== OTHERS_KEY_STR);
    // 대기 그룹을 골랐으면 검색 후보는 **그 그룹의 대기만**(2026-10-16) — 지금 그래프에 보이는 것을 앞에, 나머지는 이 DB 의 대기 목록(V$EVENT_NAME)에서.
    // 목록을 못 받으면 아는 것(보이는 대기의 그룹 · 대기 설명표의 그룹)으로 거른다
    const suggestionsFor = (wc, shown, classMap) => {
        if (!wc) return waitSuggestions;
        const inClass = shown.filter(e => e !== OTHERS_KEY_STR && classMap && classMap[e] === wc);
        const listed = waitNames[wc] || getWaitEventInfo(t).filter(w => w.waitClass === wc).map(w => w.event);
        return [...new Set([...inClass, ...listed])];
    };
    // 범례 설명(2026-10-27 사용자 요청) — 대기는 이벤트 설명(아는 것) + 대기 그룹 설명, 아래는 메트릭 설명 · SQL 앞부분 · 세션/모듈의 뜻
    const waitInfoMap = useMemo(() => Object.fromEntries(getWaitEventInfo(t).map(w => [w.event, w.description])), [t]);
    const waitHint = (key, classMap) => {
        if (key === OTHERS_KEY_STR) return t('monitoring:perf.hint.others');
        if (histWaitsPrefix && String(key).startsWith(histWaitsPrefix)) return t('monitoring:perf.hint.waits');
        if (key === 'ON CPU') return t('monitoring:perf.hint.onCpu');
        const cls = classMap && classMap[key];
        const clsText = cls && WAIT_CLASS_DESC_KEY[cls] ? `[${cls}] ${t(`monitoring:perf.wclass.${WAIT_CLASS_DESC_KEY[cls]}`)}` : null;
        const own = waitInfoMap[key];
        return [own, clsText].filter(Boolean).join('\n') || null;
    };
    const lowerHint = (mode, key) => {
        if (key === OTHERS_KEY_STR) return t('monitoring:perf.hint.othersDim');
        if (mode === 'metric') return getMetricDescriptions(t)[key] || null;
        if (mode === 'sql') {
            const head = sqlHeads[key];
            return [t(onOpenSqlHistory ? 'monitoring:perf.hint.sqlClick' : 'monitoring:perf.hint.sql'), head ? head : null].filter(Boolean).join('\n');
        }
        if (mode === 'session') return t('monitoring:perf.hint.session');
        if (mode === 'module') return t('monitoring:perf.hint.module');
        return null;
    };
    const renderHintToggle = (which, on, set) => (
        <label className="dbmsperf-hint-toggle" title={t('monitoring:perf.hint.toggleHint')}>
            <input type="checkbox" checked={on} onChange={(e) => { set(e.target.checked); writeHintPref(which, e.target.checked); }} />
            {t('monitoring:perf.hint.toggle')}
        </label>
    );
    // 비중 / 합계 시간 / 평균 시간(2026-10-16). where = 'rt'(실시간 — 평균은 AAS) | 'hist'(이력 — 평균은 1회당 ms) — 뜻이 달라 툴팁이 말한다
    const renderScaleSeg = (value, setValue, where) => (
        <span className="dbmsperf-seg dbmsperf-scale-seg" role="tablist" aria-label={t('monitoring:perf.scale.label')}>
            {['pct', 'total', 'avg'].map(v => (
                <button key={v} type="button" role="tab" aria-selected={value === v} title={t(`monitoring:perf.scale.hint.${where}.${v}`)}
                    className={`dbmsperf-seg-btn${value === v ? ' on' : ''}`} onClick={() => setValue(v)}>
                    {/* 실시간의 「평균」 은 평균 활성 세션(AAS) 그 자체다 — 이름도 그렇게(2026-11-03 시험 피드백) */}
                    {t(where === 'rt' && v === 'avg' ? 'monitoring:perf.scale.aas' : `monitoring:perf.scale.${v}`)}
                </button>
            ))}
        </span>
    );
    // 시간으로 볼 때는 「기타」 도 쌓는다 — 높이가 곧 전체 부하다(비중에서는 위쪽 빈 공간이 그 몫이라 그리지 않는다)
    // 시간일 때 세로축 이름은 달지 않는다 — 축 칸 폭이 위아래 맞춤으로 고정이라 긴 눈금(0.0225)과 겹쳤다. 단위는 제목 옆 설명이 말한다
    const withOthers = (keys, all, on) => (on && all.includes(OTHERS_KEY_STR) ? [...keys, OTHERS_KEY_STR] : keys);
    const histMetricCharts = useMemo(() => perInst(histMetricByInst, historyMetricData, null), [histMetricByInst, historyMetricData, instPick]); // eslint-disable-line react-hooks/exhaustive-deps
    // 노드 이름표 + 그래프 — 노드가 여럿이면 그래프마다 위에 「노드 n」, 높이는 조금 줄인다. render(data, 높이, 그 노드의 계열)
    const renderInstCharts = (list, height, render) => list.map(({ inst, data, keys }) => (
        <div key={inst ?? 'all'} className="dbmsperf-lock-inst-chart">
            {inst != null && <div className="dbmsperf-lock-inst-label">{t('monitoring:perf.lockInst', { n: inst })}</div>}
            {render(data, list.length > 1 ? Math.max(130, Math.round(height * 0.7)) : height, keys)}
        </div>
    ));
    // 노드 그래프들의 세로축 — 같은 최대값(정하지 않았으면 노드 데이터의 최대, 끌었으면 그 값). 한 장이면 그래프가 스스로 한다
    const maxAcross = (list, fieldsOf, stacked) => {
        let m = 0;
        list.forEach(({ data, keys }) => {
            const fs = fieldsOf(keys) || [];
            (data || []).forEach((row) => {
                if (stacked) {
                    let sum = 0;
                    fs.forEach((f) => { const v = Number(row[f]); if (Number.isFinite(v) && v > 0) sum += v; });
                    if (sum > m) m = sum;
                } else {
                    fs.forEach((f) => { if (row[f] == null || row[f] === '') return; const v = Number(row[f]); if (Number.isFinite(v) && v > m) m = v; });
                }
            });
        });
        return m > 0 ? m : undefined;
    };
    const yCtl = (group, ctx, list, leftOf, rightOf, stacked) => {
        if (list.length < 2) return {};
        const cur = yMaxes[group] && yMaxes[group].ctx === ctx ? yMaxes[group] : null;
        const setSide = (side) => (v) => setYMaxes(prev => {
            const c = prev[group] && prev[group].ctx === ctx ? prev[group] : { ctx };
            return { ...prev, [group]: { ...c, [side]: v } };
        });
        return {
            yMax: cur ? (cur.l ?? null) : null, onYMaxChange: setSide('l'),
            yMaxRight: cur ? (cur.r ?? null) : null, onYMaxRightChange: setSide('r'),
            yMaxBase: maxAcross(list, leftOf, stacked),
            yMaxRightBase: rightOf ? maxAcross(list, rightOf, false) : undefined,
        };
    };
    // 노드 고르기 리본 — 「전체」 + 노드마다. 누르면 그 노드를 넣고 빼고, 다 고르면 「전체」 로 돌아간다
    const toggleInst = (i, insts) => setInstPick(prev => {
        const next = prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i];
        return next.length === 0 || next.length === insts.length ? [] : next;
    });
    const renderInstRibbon = (lists) => {
        const insts = [...new Set(lists.filter(l => Array.isArray(l) && l.length > 1).flat().map(x => x.inst))].filter(v => v != null).sort((a, b) => a - b);
        if (insts.length < 2) return null;
        return (
            <span className="dbmsperf-seg dbmsperf-inst-seg" role="group" aria-label={t('monitoring:perf.inst.label')} title={t('monitoring:perf.inst.hint')}>
                <button type="button" aria-pressed={!instPick.length} className={`dbmsperf-seg-btn${!instPick.length ? ' on' : ''}`} onClick={() => setInstPick([])}>
                    {t('monitoring:perf.inst.all')}
                </button>
                {insts.map(i => (
                    <button key={i} type="button" aria-pressed={instPick.includes(i)} className={`dbmsperf-seg-btn${instPick.includes(i) ? ' on' : ''}`} onClick={() => toggleInst(i, insts)}>
                        {t('monitoring:perf.lockInst', { n: i })}
                    </button>
                ))}
            </span>
        );
    };

    if (!isOpen) return null;

    // 메트릭 리본 — 고른 대기에 **매핑된 지표**를 칩으로 세우고, 누른 것만 아래 그래프에 그린다(실시간·이력 공통).
    // 단위가 같으면 한 축에 여럿, 다른 단위는 오른쪽 보조 축 하나까지 — 세 번째 단위의 칩은 눌리지 않는다(그릴 축이 없다).
    const metricDesc = getMetricDescriptions(t);
    const renderMetricRibbon = ({ picked, map, cands, shown, toggle, ax }) => (
        <div className="dbmsperf-ribbon">
            <span className="dbmsperf-label" title={t(`monitoring:perf.map.level.${map.level}`)}>
                {picked ? t('monitoring:perf.ribbon.forEvent', { event: picked }) : t('monitoring:perf.ribbon.noEvent')}
            </span>
            {cands.list.map((name) => {
                const on = shown.includes(name);
                const ok = on || canShow(shown, name, unitOf);
                const unit = unitOf(name);
                return (
                    <button
                        key={name} type="button"
                        className={`dbmsperf-ribbon-chip${on ? ' on' : ''}${on && ax.right.includes(name) ? ' right' : ''}`}
                        disabled={!ok} aria-pressed={on}
                        title={!ok ? t('monitoring:perf.ribbon.unitLimit', { max: MAX_SHOWN })
                            : [metricDesc[name], unit ? t('monitoring:perf.ribbon.unit', { unit }) : ''].filter(Boolean).join(' · ') || name}
                        onClick={() => toggle(name)}
                    >
                        {name}
                    </button>
                );
            })}
            {!cands.list.length && <span className="dbmsperf-empty-note">{catalog.length ? t('monitoring:perf.ribbon.none') : t('monitoring:perf.noCatalog')}</span>}
            {cands.missing > 0 && <span className="dbmsperf-collapse-note" title={t('monitoring:perf.ribbon.missingHint')}>{t('monitoring:perf.ribbon.missing', { count: cands.missing })}</span>}
            <button type="button" className="dbmsperf-metric-edit-btn" onClick={() => goSettings('metric', picked || '*')} title={t('monitoring:perf.map.openHint')}>
                <FiLink size={11} /> {t('monitoring:perf.map.open')}
            </button>
        </div>
    );
    // 메트릭 그래프의 패널 — 왼쪽 축은 먼저 고른 것의 단위, 다른 단위는 오른쪽 보조 축(utils/waitMetricMap.splitByUnit)
    const metricPanelOf = (ax) => ({
        chartType: 'line',
        xField: 't',
        yFields: [...ax.left, ...ax.right],
        yFieldsConfig: ax.right.reduce((a, m) => { a[m] = { axis: 'right' }; return a; }, {}),
        // 축 제목은 두지 않는다 — 단위 글이 길어(「CentiSeconds Per Second」) 세워 적으면 눈금 숫자와 겹친다. 단위는 그래프 제목 줄에 적는다
        yAxisRight: ax.right.length ? {} : undefined,
    });

    // 대기이벤트 정보 아이콘 — 대기 그룹 select 옆에 붙는다. 현재 선택된 그룹이 있으면
    // 그 그룹을 목록 맨 위로 올려 바로 보이게 한다(찾아 스크롤할 필요 없도록).
    const renderWaitInfoButton = () => (
        <button
            type="button"
            className="dbmsperf-info-btn"
            title={t('monitoring:perf.showWaitDesc')}
            onClick={() => setShowWaitInfo(true)}
        >
            <FiInfo size={13} />
        </button>
    );

    const waitInfoGroups = WAIT_CLASSES
        .map(cls => ({ cls, events: getWaitEventInfo(t).filter(w => w.waitClass === cls) }))
        .filter(g => g.events.length > 0);

    const waitInfoPopover = showWaitInfo && (
        <div className="dbmsperf-waitinfo-overlay" onClick={() => setShowWaitInfo(false)}>
            <div className="app-modal dbmsperf-waitinfo-panel" data-app-modal onClick={(e) => e.stopPropagation()}>
                <div className="app-modal-header dbmsperf-waitinfo-header">
                    <span className="app-modal-title">{t('monitoring:perf.waitDescTitle')}</span>
                    <button type="button" className="app-modal-close dbmsperf-close" onClick={() => setShowWaitInfo(false)}>
                        <FiX size={16} />
                    </button>
                </div>
                <div className="dbmsperf-waitinfo-body">
                    {waitInfoGroups.map(g => (
                        <div key={g.cls} className="dbmsperf-waitinfo-class">
                            <div className="dbmsperf-waitinfo-class-title">{g.cls}</div>
                            {g.events.map(w => (
                                <div key={w.event} className="dbmsperf-waitinfo-row">
                                    <span className="dbmsperf-waitinfo-event">{w.event}</span>
                                    <span className="dbmsperf-waitinfo-desc">{w.description}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                    <div className="dbmsperf-waitinfo-footnote">
                        {t('monitoring:perf.note.waitDescScope')}
                    </div>
                </div>
            </div>
        </div>
    );

    // 락 대기 트리 한 노드 — 재귀 렌더. 루트(맨 위 차단자)는 강조색, 그 아래는 대기 체인이다.
    // 체인이 깊어져도(A→B→C) 들여쓰기만으로 관계가 읽히게 한다 — 별도 연결선은 안 그린다
    // (SVG/캔버스 없이 CSS 만으로 계단식 border-left 를 쓰는 편이 다크모드 대응도 간단하다).
    const renderLockNode = (node) => {
        const nodeKey = `${node.inst ?? ''}-${node.sid}-${node.serial ?? ''}`;
        const isBlocker = (node.children || []).length > 0;
        return (
            <li key={nodeKey} className="dbmsperf-locknode">
                <div className={`dbmsperf-locknode-row ${node.isRoot ? 'is-root' : 'is-waiter'}`}>
                    <span className="dbmsperf-locknode-icon">
                        {node.isRoot ? <FiLock size={12} /> : <FiClock size={12} />}
                    </span>
                    {/* 세션의 키는 (노드, SID, SERIAL#) — RAC 에서는 노드가 다르면 다른 세션이다 */}
                    {node.inst != null && <span className="dbmsperf-locknode-inst" title={t('monitoring:perf.lockInstHint')}>{t('monitoring:perf.lockInst', { n: node.inst })}</span>}
                    <span className="dbmsperf-locknode-sid">
                        SID:{node.sid}{node.serial != null ? `,${node.serial}` : ''}
                    </span>
                    <span className="dbmsperf-locknode-role">
                        {node.isRoot ? (node.isIdleBlocker ? t('monitoring:perf.blockerIdle') : t('monitoring:perf.blocker')) : (isBlocker ? t('monitoring:perf.waitingAndBlocking') : t('monitoring:perf.waiting'))}
                    </span>
                    {/* 차단자는 지금 상태(gv$session) — 없으면 이미 끝난 세션 */}
                    {isBlocker && node.status && (
                        <span className={`dbmsperf-locknode-status st-${String(node.status).toLowerCase()}`} title={t('monitoring:perf.lockStatusHint')}>
                            {node.status === 'GONE' ? t('monitoring:perf.lockGone') : node.status}
                        </span>
                    )}
                    {node.lockType && (
                        <span
                            className="dbmsperf-locknode-type"
                            title={lockDescOf(t, node.lockType) || getLockTypeDescriptions(t)[node.lockType] || t('monitoring:perf.noDesc')}
                        >
                            {node.lockType}
                        </span>
                    )}
                    {node.event && <span className="dbmsperf-locknode-event">{node.event}</span>}
                    {node.sqlId && (
                        <button
                            type="button"
                            className={`dbmsperf-locknode-sql${lockSql && lockSql.sqlId === node.sqlId ? ' on' : ''}`}
                            title={t('monitoring:perf.lockSql.clickHint')}
                            onClick={() => openLockSql(node)}
                        >
                            {node.sqlId}
                        </button>
                    )}
                    {node.maxWaitSec != null && (
                        <span className="dbmsperf-locknode-wait">{t('monitoring:perf.maxWait', { sec: node.maxWaitSec })}</span>
                    )}
                    {node.samples != null && (
                        <span className="dbmsperf-locknode-samples" title={t(isLockHist ? 'monitoring:perf.lockHist.samplesHint' : 'monitoring:perf.lockSamplesHint')}>
                            {node.samples}{t('monitoring:perf.sample')}
                            {node.firstSeen && <span className="dbmsperf-locknode-span"> {hms(node.firstSeen)}~{hms(node.lastSeen)}</span>}
                        </span>
                    )}
                    {isBlocker && node.blockedSamples > 0 && (
                        <span className="dbmsperf-locknode-blocked" title={t('monitoring:perf.lockBlockedHint')}>{t('monitoring:perf.lockBlocked', { n: node.blockedSamples })}</span>
                    )}
                </div>
                {/* 세션 정보 — 모든 세션에 program · machine · module, 경합 대상 객체는 차단자 줄에만 */}
                {(node.program || node.machine || node.module || (isBlocker && (node.objects || []).length > 0)) && (
                <div className={`dbmsperf-locknode-meta ${node.isRoot ? 'is-root' : ''}`}>
                    {/* 모르는 칸은 비운다 — 표본도 지금 세션도 없는 차단자(이미 끝남)는 셋 다 모른다 */}
                    {node.program && <span title="PROGRAM">{node.program}</span>}
                    {node.machine && <span title="MACHINE">{node.machine}</span>}
                    {node.module && <span title="MODULE">{node.module}</span>}
                    {isBlocker && (node.objects || []).length > 0 && (
                        <span className="dbmsperf-locknode-objs" title={t('monitoring:perf.lockObjHint')}>
                            {t('monitoring:perf.lockObj')}: {node.objects.map(o => o.name || `obj# ${o.id}`).join(', ')}
                        </span>
                    )}
                </div>
                )}
                {node.children && node.children.length > 0 && (
                    <ul className="dbmsperf-locktree-children">
                        {node.children.map(renderLockNode)}
                    </ul>
                )}
            </li>
        );
    };

    // 100% 스택 — 절대 AAS 가 아니라 각 항목의 비중(%)을 쌓는다. 상위1~4 + 기타를 합치면
    // 정의상 항상 100% 이므로 그래프 윗부분은 항상 수평이다("상위 N개만 표시" 가 아니라
    // "전체를 100%로 보고 그 구성비" 가 목표 — 팀장 피드백 반영).
    // 기타는 항상 맨 위에 쌓이도록 seriesKeys 순서를 그대로 쓰고, 색은 회색 계열로 고정해
    // '개별 항목' 이 아니라 '나머지 묶음' 임이 보이게 한다.
    // 대기 수 점선(보조 축) — 이력에서 **대기 하나를 골랐을 때만**, 합계·평균 시간일 때만(비중은 100% 기준이라 횟수와 나란히 읽을 뜻이 없다).
    // 여러 대기를 그릴 때는 그리지 않는다(사용자 결정 2026-10-16). 실시간은 ASH 에 대기별 횟수가 없어 그리지 않는다
    // 대기 하나 = 그래프에서 고른 대기, 또는 검색으로 좁혀 남은 대기가 하나일 때 그 대기
    const histSearchOnly = histSearch ? historyWaitSeries.filter(k => k !== OTHERS_KEY_STR) : [];
    const histWaitTarget = histPicked || (histSearchOnly.length === 1 ? histSearchOnly[0] : null);
    const histWaitsLine = histScale !== 'pct' && histWaitTarget && histWaitsPrefix && historyWaitSeries.includes(histWaitTarget) ? `${histWaitsPrefix}${histWaitTarget}` : null;
    const waitsFieldConfig = (label) => ({ axis: 'right', dashed: true, name: label, color: darkMode ? '#e5e7eb' : '#374151' });
    // 눌러 고른 상태면 범례는 누르기 전 목록(색도 그 목록 기준 — 바뀌면 같은 대기의 색이 달라 보인다), 고른 것 말고는 흐리게
    const ashClassAll = ashBase ? { ...ashBase.cls, ...(ashWaitClass || {}) } : ashWaitClass;
    const ashLegendKeys = ashBase ? ashBase.keys : ashSeries.filter(k => k !== OTHERS_KEY_STR);
    // 색은 합계와 모든 노드의 계열을 함께 놓고 정한다 — 노드마다 정하면 같은 대기가 노드마다 다른 색이 된다
    const ashColorKeys = [...new Set([...ashLegendKeys, ...ashByInst.flatMap(x => x.seriesKeys || [])])].filter(k => k !== OTHERS_KEY_STR);
    const ashPanelFor = (keys) => { const series = keys || ashSeries; return {
        // 비중·합계 시간은 쌓는 영역, 평균 시간은 선(2026-10-16 사용자 결정)
        chartType: rtScale === 'avg' ? 'line' : 'stackedarea',
        xField: 't',
        // 기타는 그리지 않는다 — 비율은 여전히 전체 기준이라 상단 여백이 그대로 「나머지」다.
        // 검색으로 좁혔으면 「기타」(검색에 걸리지 않은 나머지)를 그리지 않는다 — 고른 대기만 본다(2026-10-16)
        yFields: ashBase ? orderWaitSeries(ashBase.keys, ashClassAll)
            : withOthers(orderWaitSeries(series, ashWaitClass), series, rtScale !== 'pct' && !ashSearch),
        dimmedFields: ashBase ? ashBase.keys.filter(k => k !== pickedEvent) : undefined,
        yFieldsConfig: {
            ...Object.entries(buildWaitClassColors(ashColorKeys, ashClassAll || {}))
                .reduce((a, [k, c]) => { a[k] = { color: c }; return a; }, {}),
            [OTHERS_KEY_STR]: { color: darkMode ? OTHERS_COLOR.dark : OTHERS_COLOR.light },
        },
        yAxis: rtScale === 'pct' ? { title: t('monitoring:perf.topShare'), min: 0, max: 100 }
            : rtScale === 'total' ? { title: t('monitoring:perf.axis.totalSec'), shortTitle: t('monitoring:perf.axis.totalSecShort'), min: 0 }
                : { title: t('monitoring:perf.axis.aas'), shortTitle: t('monitoring:perf.axis.aasShort'), min: 0 },
    }; };
    // 아래 그래프(SQL·세션·모듈) — 위와 같은 읽기 방식(100% 스택). 대기 그룹 색은 없다(그 기준에는 그룹 개념이 없다)
    const dimPanelFor = (keys) => { const series = keys || dimSeries; return {
        // 비중·합계 시간은 쌓는 영역, AAS(평균)는 선(2026-10-16 사용자 결정 — 영역은 비율·합계에만)
        chartType: rtScale === 'avg' ? 'line' : 'stackedarea',
        xField: 't',
        // 「SQL 없음」·「모듈 없음」 은 그리지 않는다 — 누를 수도 없고(가리키는 SQL·모듈이 없다) 소음이다(2026-10-14 사용자 의견).
        // 비율은 **여전히 전체 합계 기준**(toPercentSeries 가 모든 계열로 나눈다) — 기타와 같이 위쪽 빈 공간이 그 몫이다. 빼고 다시 100% 를 만들면 나머지가 부풀려진다
        yFields: withOthers(orderWaitSeries(series, null).filter(k => !NON_DRAWN_DIM_KEYS.includes(k)), series, rtScale !== 'pct'),
        yFieldsConfig: { [OTHERS_KEY_STR]: { color: darkMode ? OTHERS_COLOR.dark : OTHERS_COLOR.light } },
        yAxis: rtScale === 'pct' ? { title: t('monitoring:perf.topShare'), min: 0, max: 100 }
            : rtScale === 'total' ? { title: t('monitoring:perf.axis.totalSec'), shortTitle: t('monitoring:perf.axis.totalSecShort'), min: 0 }
                : { title: t('monitoring:perf.axis.aas'), shortTitle: t('monitoring:perf.axis.aasShort'), min: 0 },
    }; };
    // 노드 그래프의 세로축 묶음 — 그릴 계열(흐리게 둔 것은 뺀다)
    const drawnOf = (panel) => panel.yFields.filter(f => !(panel.dimmedFields || []).includes(f));
    const ashY = yCtl('ash', rtScale, ashCharts, keys => drawnOf(ashPanelFor(keys)), null, rtScale !== 'avg');
    const dimY = yCtl('dim', `${rtScale}|${lowerMode}`, dimCharts, keys => drawnOf(dimPanelFor(keys)), null, rtScale !== 'avg');
    const sysY = yCtl('sys', shownKey, sysCharts, () => axes.left, () => axes.right, false);
    /** 관련 설정 표 — 값은 **지금 설정**이다(그 대기가 났던 때의 값이 아닐 수 있다). 「왜 보나」 는 코드(why)를 번역해 붙인다 */
    const settingValue = (it) => {
        if (it.missing) return '-';
        if (it.kind === 'redo' && it.name === 'redo_logs') {
            return (it.detail || []).map(d => t('monitoring:perf.set.redoLine', {
                thread: d.thread, groups: d.groups, size: d.minMb === d.maxMb ? `${d.maxMb}MB` : `${d.minMb}~${d.maxMb}MB`, members: d.members, busy: d.busy,
            })).join(' / ') || '-';
        }
        if (it.kind === 'redo' && it.name === 'switch_rate') {
            const d = it.detail || {};
            if (d.inRange !== undefined) {   // 성능 이력 — 그 구간의 전환
                return t('monitoring:perf.set.switchLineHist', { total: d.inRange ?? '-', per: d.perHour ?? '-', max: d.maxHour ?? '-' })
                    + (d.partial ? ` ${t('monitoring:perf.set.switchPartial', { from: String(d.keptFrom || '').replace('T', ' ') })}` : '');
            }
            return t('monitoring:perf.set.switchLine', { hour: d.lastHour ?? '-', max: d.maxHour ?? '-', day: d.lastDay ?? '-' });
        }
        return it.value == null || it.value === '' ? t('monitoring:perf.set.empty') : String(it.value);
    };
    // 성능 이력이면 이름표도 이력의 출처로(2026-10-22 — 값은 DBA_HIST_* 에서 읽는데 이름표가 V$… 라 동적 뷰 값으로 읽혔다)
    const settingName = (it, hist) => (it.kind === 'param' ? it.name : t(`monitoring:perf.set.${hist ? 'itemHist' : 'item'}.${it.name}`));
    // 관련 설정 표 — 실시간·이력이 함께 쓴다(고른 대기·받은 표·불러오는 중은 각자의 것을 넘긴다)
    const renderRelatedSettings = ({ picked, data: relSettings, loading }) => (
        <>
            <div className="dbmsperf-chart-title">
                {picked ? t('monitoring:perf.set.title', { event: picked }) : t('monitoring:perf.set.titleGeneral')}
                <button type="button" className="dbmsperf-metric-edit-btn" onClick={() => goSettings('param', (relSettings && relSettings.key) || picked || '*')} title={t('monitoring:perf.cfg.editParamHint')}>
                    {t('monitoring:perf.map.open')}
                </button>
                {relSettings && relSettings.source === 'user' && <span className="dbmsperf-chart-note"> {t('monitoring:perf.cfg.usingMine')}</span>}
                {relSettings?.isDemo && <span className="dbmsperf-chart-note"> {t('monitoring:perf.set.demo')}</span>}
                {loading && <span className="dbmsperf-collapse-note">{t('monitoring:perf.loading')}</span>}
            </div>
            <div className="dbmsperf-collapse-note">
                {relSettings?.asOf
                    ? t('monitoring:perf.set.histNote', { at: String(relSettings.asOf.snapTime || relSettings.asOf.toT || '').replace('T', ' ') })
                    : t('monitoring:perf.set.nowNote')}
            </div>
            <table className="dbmsperf-settings-table">
                <thead>
                    <tr>
                        <th>{t('monitoring:perf.set.colItem')}</th>
                        <th>{relSettings?.asOf ? t('monitoring:perf.set.colValueHist') : t('monitoring:perf.set.colValue')}</th>
                        <th>{t('monitoring:perf.set.colWhy')}</th>
                    </tr>
                </thead>
                <tbody>
                    {(relSettings?.items || []).map(it => (
                        <tr key={`${it.kind}:${it.name}`}>
                            <td className="dbmsperf-settings-name" title={it.description || undefined}>{settingName(it, !!relSettings?.asOf)}</td>
                            <td className="dbmsperf-settings-value">
                                {settingValue(it)}
                                {it.kind === 'param' && !it.missing && (it.isDefault
                                    ? <span className="dbmsperf-settings-badge"> {t('monitoring:perf.set.default')}</span>
                                    : <span className="dbmsperf-settings-badge changed"> {t('monitoring:perf.set.changed')}</span>)}
                                {/* 성능 이력 — 구간 중에 값이 바뀌었는가 · 지금은 다른가 · AWR 에 이력이 없어 지금 값인가 */}
                                {it.changedInRange && <span className="dbmsperf-settings-badge changed" title={t('monitoring:perf.set.changedInRangeHint')}> {t('monitoring:perf.set.changedInRange')}</span>}
                                {it.current != null && <span className="dbmsperf-settings-badge"> {t('monitoring:perf.set.nowValue', { value: it.current })}</span>}
                                {it.nowOnly && relSettings?.asOf && <span className="dbmsperf-settings-badge" title={t('monitoring:perf.set.nowOnlyHint')}> {t('monitoring:perf.set.nowOnly')}</span>}
                            </td>
                            <td className="dbmsperf-settings-why">{t(`monitoring:perf.set.why.${it.why}`)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {(relSettings?.notes || []).map(n => <div key={n} className="dbmsperf-collapse-note">
                {n === 'NO_SNAPSHOT' ? t('monitoring:perf.set.noSnapshot') : t('monitoring:perf.set.failed', { what: n })}
            </div>)}
        </>
    );
    const sysmetricPanelRealtime = metricPanelOf(axes);
    const sysmetricPanelHistory = metricPanelOf(histAxes);
    // 가로축 범위 — 위아래가 **같은 값**을 받는다. 아래에 오른쪽 축이 서면 위에도 같은 폭을 비워 그림 영역의 양 끝을 맞춘다
    // 가로축 — 서버가 실제로 쓴 구간이 있으면 그것(끌어 고른 구간·전체 기간), 없으면 DB 의 지금에서 최근 N분
    const rtDomain = spanDomain(rtSpan) || domainOf(rtNow, typeof windowMin === 'number' ? windowMin : 120);
    const rtWindowMin = spanMinutes(rtSpan) || (typeof windowMin === 'number' ? windowMin : 120);
    // 위(대기 수 선)나 아래(메트릭 보조 축) 어느 한쪽에라도 오른쪽 축이 서면 둘 다 같은 폭을 비운다
    const rtRightWidth = (lowerMode === 'metric' && axes.right.length) ? PAIRED_AXIS_WIDTH : undefined;
    const histRightWidth = (histAxes.right.length || histWaitsLine) ? PAIRED_AXIS_WIDTH : undefined;

    // 대기이벤트 이력 — 실시간 성능과 같은 읽기 방식(100% 스택 + 기타)으로 맞춘다.
    // 같은 개념을 두 탭에서 다른 모양으로 그리면 눈이 매번 다시 적응해야 한다.
    const histClassAll = histBase ? { ...histBase.cls, ...(historyWaitClassMap || {}) } : historyWaitClassMap;
    const histLegendKeys = histBase ? histBase.keys : historyWaitSeries.filter(k => k !== OTHERS_KEY_STR);
    // 성능 이력 아래의 SQL·세션·모듈 — 위 눈금(비중·합계 시간 = 영역, 평균 시간 = AAS 선)을 그대로 따른다
    const shapeHist = (data, series) => (histScale === 'pct' ? toPercentSeries(data, series)
        : histScale === 'total' ? scaleSeries(data, series, histDimBucketSec || 1) : data);
    const histDimCharts = perInst(histDimByInst, shapeHist(histDimData, histDimSeries), x => shapeHist(x.data, histDimSeries));
    const histDimPanel = {
        chartType: histScale === 'avg' ? 'line' : 'stackedarea',
        xField: 't',
        yFields: withOthers(orderWaitSeries(histDimSeries, null).filter(k => !NON_DRAWN_DIM_KEYS.includes(k)), histDimSeries, histScale !== 'pct'),
        yFieldsConfig: { [OTHERS_KEY_STR]: { color: darkMode ? OTHERS_COLOR.dark : OTHERS_COLOR.light } },
        yAxis: histScale === 'pct' ? { title: t('monitoring:perf.topShare'), min: 0, max: 100 }
            : histScale === 'total' ? { title: t('monitoring:perf.axis.totalSec'), shortTitle: t('monitoring:perf.axis.totalSecShort'), min: 0 }
                : { title: t('monitoring:perf.axis.aas'), shortTitle: t('monitoring:perf.axis.aasShort'), min: 0 },
    };
    const histColorKeys = [...new Set([...histLegendKeys, ...histWaitByInst.flatMap(x => x.seriesKeys || [])])].filter(k => k !== OTHERS_KEY_STR);
    const historyWaitPanelFor = (keys) => { const series = keys || historyWaitSeries; return {
        // 평균(1회당 ms)은 더할 수 없는 값이라 쌓지 않고 선으로 — 쌓으면 높이가 「평균의 합」 이라는 뜻 없는 값이 된다
        chartType: histScale === 'avg' ? 'line' : 'stackedarea',
        xField: 't',
        yFields: [...(histBase ? orderWaitSeries(histBase.keys, histClassAll)
            : withOthers(orderWaitSeries(series, historyWaitClassMap), series, histScale !== 'pct' && !histSearch)),
        ...(histWaitsLine ? [histWaitsLine] : [])],
        dimmedFields: histBase ? histBase.keys.filter(k => k !== histPicked) : undefined,
        yFieldsConfig: {
            ...Object.entries(buildWaitClassColors(histColorKeys, histClassAll || {}))
                .reduce((a, [k, c]) => { a[k] = { color: c }; return a; }, {}),
            [OTHERS_KEY_STR]: { color: darkMode ? OTHERS_COLOR.dark : OTHERS_COLOR.light },
            ...(histWaitsLine ? { [histWaitsLine]: waitsFieldConfig(t('monitoring:perf.waits.ofEvent', { event: histWaitTarget })) } : {}),
        },
        yAxisRight: histWaitsLine ? { title: t('monitoring:perf.waits.perSnap'), shortTitle: t('monitoring:perf.waits.short'), min: 0 } : undefined,
        yAxis: histScale === 'pct' ? { title: t('monitoring:perf.topShare'), min: 0, max: 100 }
            : histScale === 'avg' ? { title: t('monitoring:perf.axis.avgMs'), shortTitle: t('monitoring:perf.axis.avgMsShort'), min: 0 }
                : { title: t('monitoring:perf.axis.waitSec'), shortTitle: t('monitoring:perf.axis.waitSecShort'), min: 0 },
    }; };
    const histWaitY = yCtl('histWait', `${histScale}|${histWaitsLine || ''}`, histWaitCharts,
        keys => drawnOf(historyWaitPanelFor(keys)).filter(f => f !== histWaitsLine), histWaitsLine ? () => [histWaitsLine] : null, histScale !== 'avg');
    const histMetricY = yCtl('histMetric', histShown.join(','), histMetricCharts, () => histAxes.left, () => histAxes.right, false);
    const histDimY = yCtl('histDim', `${histScale}|${histLowerMode}`, histDimCharts, () => drawnOf(histDimPanel), null, histScale !== 'avg');
    // 메트릭 추이와 대기이벤트 추이는 **같은 AWR 스냅샷 구간**을 본다 —
    // 축이 각자 데이터 범위로 잡히면 같은 시각이 두 그래프에서 다른 x 위치에 찍혀 대조가 안 된다.
    const historyWindowMin = spanMinutes(histSpan) || (typeof historyDays === 'number' ? historyDays : 14) * 24 * 60;
    const histDomain = spanDomain(histSpan) || domainOf(histNow, historyWindowMin);

    // 락/enqueue 추이 — 실시간 대기이벤트 추이(ashPanel)와 같은 누적+기타 규칙, 색만
    // "락종류→wait class" 매핑(LOCK_TYPE_CLASS)을 거쳐 같은 색상군 규칙을 쓴다.
    // 락 추이도 같은 읽기 규칙을 따른다 — 한 쪽에만 기타가 남아 있으면
    // 같은 모양의 그래프가 탭마다 다르게 읽힌다.
    // 색은 합계와 모든 노드의 계열을 함께 놓고 정한다 — 노드마다 정하면 같은 락 종류가 노드마다 다른 색이 된다
    const lockColorKeys = [...new Set([...lockTrendSeries, ...lockTrendByInst.flatMap(x => x.seriesKeys || [])])].filter(k => k !== OTHERS_KEY_STR);
    const lockTrendPanelFor = (keys) => ({
        // AAS 는 선, 세션 수 · 합계 시간은 쌓는 영역 — 실시간 성능과 같은 규칙(영역은 합계에만, 2026-10-16 사용자 결정)
        chartType: lockScale === 'aas' ? 'line' : 'stackedarea',
        xField: 't',
        yFields: orderWaitSeries(keys || lockTrendSeries, null),
        yFieldsConfig: {
            ...Object.entries(buildWaitClassColors(lockColorKeys, LOCK_TYPE_CLASS))
                // 같은 대기 그룹(TX·TM 은 둘 다 Application)이면 색이 거의 같아 겹친 영역을 가를 수 없었다 — 주요 락 종류는 고정 색(2026-10-14)
                .reduce((a, [k, c]) => { a[k] = { color: LOCK_TYPE_COLORS[k] || c }; return a; }, {}),
            [OTHERS_KEY_STR]: { color: darkMode ? OTHERS_COLOR.dark : OTHERS_COLOR.light },
        },
        yAxis: lockScale === 'sessions' ? { title: t('monitoring:perf.lockScale.axis'), shortTitle: t('monitoring:perf.lockScale.axisShort'), min: 0 }
            : lockScale === 'total' ? { title: t('monitoring:perf.axis.totalSec'), shortTitle: t('monitoring:perf.axis.totalSecShort'), min: 0 }
                : { title: t('monitoring:perf.axis.aas'), shortTitle: t('monitoring:perf.axis.aasShort'), min: 0 },
    });
    // 노드 그래프들은 같은 세로축 최대값 — 한 노드에서 끌면 다른 노드도 함께(실시간 성능과 같은 장치 yCtl)
    const lockY = yCtl('lock', `${lockSrc}|${lockScale}`, lockCharts, keys => drawnOf(lockTrendPanelFor(keys)), null, lockScale !== 'aas');
    const lockDomain = isLockHist ? spanDomain(lockSpan) : domainOf(lockNow, lockWindowMin);
    const lockWinMin = isLockHist ? (spanMinutes(lockSpan) || lockDays * 24 * 60) : lockWindowMin;
    const renderLockScaleSeg = () => (
        <span className="dbmsperf-seg dbmsperf-scale-seg" role="tablist" aria-label={t('monitoring:perf.scale.label')}>
            {LOCK_SCALES.map(v => (
                <button key={v} type="button" role="tab" aria-selected={lockScale === v} title={t(`monitoring:perf.lockScale.hint.${v}`)}
                    className={`dbmsperf-seg-btn${lockScale === v ? ' on' : ''}`} onClick={() => setLockScale(v)}>
                    {t(`monitoring:perf.lockScale.${v}`)}
                </button>
            ))}
        </span>
    );
    // 락 종류 설명 — 대기이벤트 설명 창과 같은 모양. 이 구간에 나타난 종류는 세션 수를 함께
    const lockInfoPopover = showLockInfo && (
        <div className="dbmsperf-waitinfo-overlay" onClick={() => setShowLockInfo(false)}>
            <div className="app-modal dbmsperf-waitinfo-panel" data-app-modal onClick={(e) => e.stopPropagation()}>
                <div className="app-modal-header dbmsperf-waitinfo-header">
                    <span className="app-modal-title">{t('monitoring:lockInfo.title')}</span>
                    <button type="button" className="app-modal-close dbmsperf-close" onClick={() => setShowLockInfo(false)} aria-label={t('monitoring:perf.close')}>
                        <FiX size={16} />
                    </button>
                </div>
                <div className="dbmsperf-waitinfo-body">
                    {LOCK_GROUPS.map(g => (
                        <div key={g.key} className="dbmsperf-waitinfo-class">
                            <div className="dbmsperf-waitinfo-class-title">{t(`monitoring:lockInfo.group.${g.key}.name`)}</div>
                            <div className="dbmsperf-lockinfo-groupdesc">{t(`monitoring:lockInfo.group.${g.key}.desc`)}</div>
                            {g.types.map(type => {
                                const seen = lockTypeCatalog.find(c => c.type === type);
                                return (
                                    <div key={type} className={`dbmsperf-waitinfo-row dbmsperf-lockinfo-row${seen ? ' is-seen' : ''}`}>
                                        <span className="dbmsperf-waitinfo-event">
                                            {type}
                                            {seen && <span className="dbmsperf-locktype-count" title={t('monitoring:perf.lockTypeCountHint')}>{seen.sessions}</span>}
                                        </span>
                                        <span className="dbmsperf-waitinfo-desc">
                                            <b>{t(`monitoring:lockInfo.type.${type}.name`)}</b> — {t(`monitoring:lockInfo.type.${type}.desc`)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <div className="dbmsperf-waitinfo-class">
                        <div className="dbmsperf-waitinfo-class-title">{t('monitoring:lockInfo.group.other.name')}</div>
                        <div className="dbmsperf-lockinfo-groupdesc">{t('monitoring:lockInfo.group.other.desc')}</div>
                    </div>
                    <div className="dbmsperf-waitinfo-footnote">{t('monitoring:lockInfo.footnote')}</div>
                </div>
            </div>
        </div>
    );

    // ── 본문 ──────────────────────────────────────────────────────────────
    // embedded 모드에서는 이 조각만 반환한다. 데모 토글은 원래 탭바에 있었는데
    // 탭바를 컨테이너가 가져갔으므로 본문 상단으로 내린다(두 탭 모두에서 보여야 한다).
    const body = (
        <>
            {/* 설정 탭은 DB 를 그리지 않으므로 예시 데이터를 두지 않는다 */}
            {embedded && activeTab !== 'settings' && (
                <div className="dbmsperf-embedded-bar">
                    <label className="dbmsperf-demo-toggle">
                        <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                        {t('monitoring:perf.demoData')}
                    </label>
                </div>
            )}
            {backendError && (
                <div className="dbmsperf-error">
                    <FiInfo size={13} /> {backendError}
                </div>
            )}
            {waitInfoPopover}
            {lockInfoPopover}
            {qvSlot && (
                <QueryViewDialog
                    title={t('monitoring:qv.titleOf', { what: t(`monitoring:qv.slot.${qvSlot}`) })}
                    items={qvSlot === 'lock' ? [...(srcSql.lockTrend || []), ...(srcSql.lockTree || [])] : (srcSql[qvSlot] || [])}
                    note={useDemo ? t('monitoring:qv.demoNote') : t('monitoring:qv.noteExecuted')}
                    onClose={() => setQvSlot(null)}
                />
            )}

            <div className={`dbmsperf-body${activeTab === 'lockwait' || activeTab === 'settings' ? ' is-fill' : ''}`}>
                {/* 설정(2026-10-16) — 대기 ↔ 시스템 메트릭 · 대기 ↔ 설정 파라미터 매핑과 공개·복제 */}
                {activeTab === 'settings' && (
                    <PerfSettingsPanel
                        api={PERF_API} target={settingsTarget} run={run}
                        metricCatalog={catalog} metricDesc={metricDesc}
                        knownEvents={[...new Set([...ashSeries, ...historyWaitSeries, ...getWaitEventInfo(t).map(w => w.event)])].filter(e => e !== OTHERS_KEY_STR)}
                        focus={settingsFocus}
                        onSaved={(m) => { setUserMap(m.map || {}); setRelSettings(null); }}
                    />
                )}
                {activeTab === 'realtime' && (
                    <div className="dbmsperf-fade-swap dbmsperf-layout">
                        <div className="dbmsperf-toolbar">
                            <select className="dbmsperf-select" value={rtRange ? 'custom' : String(windowMin)}
                                onChange={(e) => { const v = e.target.value; if (v === 'custom') return; setRtRange(null); setWindowMin(v === 'all' ? 'all' : Number(v)); }}>
                                {getWindowOptions(t).map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                                <option value="all">{t('monitoring:range.all')}</option>
                                {rtRange && <option value="custom">{t('monitoring:range.custom', { range: rangeLabel(rtRange) })}</option>}
                            </select>
                            {rtRange ? (
                                <button type="button" className="dbmsperf-btn dbmsperf-btn-secondary dbmsperf-range-clear" onClick={() => setRtRange(null)} title={t('monitoring:range.clearHint')}>
                                    <FiX size={12} />{t('monitoring:range.clear')}
                                </button>
                            ) : <span className="dbmsperf-toolbar-hint">{t('monitoring:range.dragHint')}</span>}
                            {renderInstRibbon([ashByInst, dimByInst, sysByInst])}
                            <WaitClassSelect value={waitClass} onChange={setWaitClass} t={t} />
                            <WaitSearch value={ashSearch} onChange={typeAshSearch} suggestions={suggestionsFor(waitClass, ashSeries, ashWaitClass)} t={t} />
                            {renderWaitInfoButton()}
                            <button className="dbmsperf-btn dbmsperf-btn-secondary" onClick={refreshRealtime} disabled={ashLoading}>
                                <FiRefreshCw size={12} className={ashLoading ? 'dbmsperf-spin' : ''} />{t('monitoring:perf.refresh')}
                            </button>
                            <label className="dbmsperf-autorefresh">
                                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                                {t('monitoring:perf.autoRefresh30')}
                            </label>
                            {/* 두 그래프의 시각은 DB 서버의 시계다 — 브라우저·앱 서버 시계가 아니다 */}
                            {rtNow && <span className="dbmsperf-toolbar-note" title={t('monitoring:perf.dbClockHint')}><FiClock size={12} /> {t('monitoring:perf.dbClock', { time: rtNow.replace('T', ' ') })}</span>}
                        </div>

                        <div className="dbmsperf-chart-card">
                            <div className="dbmsperf-chart-title dbmsperf-chart-title-fixed">
                                <span className="dbmsperf-chart-title-text" onMouseEnter={showFullTitle}>
                                    {t('monitoring:perf.h.waitTrend')}
                                    <span className="dbmsperf-chart-note"> {t(rtScale === 'pct' ? 'monitoring:perf.note.ash' : rtScale === 'total' ? 'monitoring:perf.note.ashTotal' : 'monitoring:perf.note.ashTime')}</span>
                                    <span className="dbmsperf-chart-note dbmsperf-click-hint"> {t('monitoring:perf.pickEventHint')}</span>
                                    {ashSearch && <span className="dbmsperf-chart-note dbmsperf-search-note"> {t(ashExact ? 'monitoring:perf.search.activeExact' : 'monitoring:perf.search.active', { q: ashSearch })}</span>}
                                    {ashMulti.length > 0 && <span className="dbmsperf-chart-note dbmsperf-search-note"> {t('monitoring:perf.others.active', { n: ashMulti.length })}</span>}
                                </span>
                                {ashMulti.length > 0 && <button type="button" className="dbmsperf-metric-edit-btn dbmsperf-multi-chip" onClick={resetAshMulti} title={t('monitoring:perf.others.reset')} aria-label={t('monitoring:perf.others.reset')}>{t('monitoring:perf.others.chip', { n: ashMulti.length })} <FiX size={11} /></button>}
                                {renderHintToggle('upper', hintUpper, setHintUpper)}
                                {renderScaleSeg(rtScale, setRtScale, 'rt')}
                                <QueryViewButton onClick={() => setQvSlot('rtWait')} />
                            </div>
                            {renderInstCharts(ashCharts, 210, (data, h, keys) => (
                                <PanelChart
                                    {...ashY}
                                    panel={ashPanelFor(keys)}
                                    data={data}
                                    height={h}
                                    isDarkMode={darkMode}
                                    timeWindowMin={rtWindowMin}
                                    timeDomain={rtDomain}
                                    onRangeSelect={selectRtRange}
                                    displayMode="sync"
                                    forcedLeftAxisWidth={PAIRED_AXIS_WIDTH}
                                    forcedRightAxisWidth={rtRightWidth}
                                    onElementClick={handleAshChartClick}
                                    legendHint={hintUpper ? (k) => waitHint(k, ashClassAll) : undefined}
                                />
                            ))}
                            <WaitOthersPicker open={othersOpen === 'rt'} drawn={ashSeries.filter(k => k !== OTHERS_KEY_STR)} othersList={ashOthers}
                                picked={ashMulti} onApply={applyAshMulti} onReset={resetAshMulti} onClose={() => setOthersOpen(null)} t={t} />
                        </div>

                        {/* 고른 대기 — 아래 그래프가 무엇에 대한 것인지 늘 보이게 한다 */}
                        <div className="dbmsperf-picked">
                            {pickedEvent ? (
                                <>
                                    <span className="dbmsperf-picked-chip">
                                        {t('monitoring:perf.picked', { event: pickedEvent })}{pickedClass ? ` · ${pickedClass}` : ''}
                                        <button type="button" onClick={() => { if (ashExact && ashSearch === pickedEvent) { setAshSearch(''); setAshExact(false); setAshBase(null); } setPickedEvent(null); }} aria-label={t('monitoring:perf.pickedClear')} title={t('monitoring:perf.pickedClear')}><FiX size={10} /></button>
                                    </span>
                                    {onSelectTop && (
                                        <button type="button" className="dbmsperf-metric-edit-btn" onClick={() => onSelectTop('wait_event', pickedEvent, runningWindowMin)} title={t('monitoring:perf.toRunningHint')}>
                                            <FiExternalLink size={11} /> {t('monitoring:perf.toRunning')}
                                        </button>
                                    )}
                                </>
                            ) : <span className="dbmsperf-collapse-note">{t('monitoring:perf.pickedNone')}</span>}
                            {renderHintToggle('lower', hintLower, setHintLower)}
                            <span className="dbmsperf-seg" role="tablist" aria-label={t('monitoring:perf.lower.label')}>
                                {getLowerModes(t).map(o => (
                                    <button key={o.value} type="button" role="tab" aria-selected={lowerMode === o.value}
                                        className={`dbmsperf-seg-btn${lowerMode === o.value ? ' on' : ''}`} onClick={() => setLowerMode(o.value)}>
                                        {o.label}
                                    </button>
                                ))}
                            </span>
                            <QueryViewButton onClick={() => setQvSlot('rtLower')} />
                        </div>

                        {lowerMode === 'settings' ? (
                            <div className="dbmsperf-chart-card dbmsperf-fade-swap" key="lower-settings">
                                {renderRelatedSettings({ picked: pickedEvent, data: relSettings, loading: lowerLoading })}
                            </div>
                        ) : lowerMode === 'metric' ? (
                            <div className="dbmsperf-chart-card dbmsperf-fade-swap" key="lower-metric">
                                {renderMetricRibbon({ picked: pickedEvent, map: mapping, cands: candidates, shown: shownMetrics, toggle: toggleShown, ax: axes })}
                                <div className="dbmsperf-chart-title">
                                    {t('monitoring:perf.h.sysmetric')}
                                    {metricScope === 'pdb' && <span className="dbmsperf-chart-note" title={t('monitoring:perf.pdbScopeHint')}> {t('monitoring:perf.pdbScope')}</span>}
                                    {shownMetrics.length > 0 && <span className="dbmsperf-chart-note"> {axes.right.length ? t('monitoring:perf.twoAxes', { left: axes.leftUnit || '-', right: axes.rightUnit || '-' }) : (axes.leftUnit ? t('monitoring:perf.oneAxis', { unit: axes.leftUnit }) : '')}</span>}
                                    {lowerLoading && <span className="dbmsperf-collapse-note">{t('monitoring:perf.loading')}</span>}
                                </div>
                                {shownMetrics.length ? (
                                    renderInstCharts(sysCharts, 200, (data, h) => (
                                        <PanelChart {...sysY} panel={sysmetricPanelRealtime} data={data} height={h} isDarkMode={darkMode} timeWindowMin={rtWindowMin} timeDomain={rtDomain} onRangeSelect={selectRtRange}
                                            displayMode="sync" forcedLeftAxisWidth={PAIRED_AXIS_WIDTH} forcedRightAxisWidth={rtRightWidth}
                                            legendHint={hintLower ? (k) => lowerHint('metric', k) : undefined} />
                                    ))
                                ) : <div className="dbmsperf-empty-note">{t('monitoring:perf.ribbon.pick')}</div>}
                            </div>
                        ) : (
                            <div className="dbmsperf-chart-card dbmsperf-fade-swap" key={`lower-${lowerMode}`}>
                                <div className="dbmsperf-chart-title">
                                    {pickedEvent
                                        ? t('monitoring:perf.h.dimForEvent', { dim: getLowerModes(t).find(o => o.value === lowerMode)?.label, event: pickedEvent })
                                        : t('monitoring:perf.aasByDim', { dim: getLowerModes(t).find(o => o.value === lowerMode)?.label })}
                                    {lowerMode !== 'session' && rtScale === 'pct' && <span className="dbmsperf-chart-note" title={t('monitoring:perf.note.dimHiddenHint')}> {t('monitoring:perf.note.dimHidden')}</span>}
                                    {onSelectTop && <span className="dbmsperf-chart-note dbmsperf-click-hint"> {t('monitoring:perf.clickAreaHint')}</span>}
                                    {lowerLoading && <span className="dbmsperf-collapse-note">{t('monitoring:perf.loading')}</span>}
                                </div>
                                {renderInstCharts(dimCharts, 220, (data, h, keys) => (
                                    <PanelChart {...dimY} panel={dimPanelFor(keys)} data={data} height={h} isDarkMode={darkMode} timeWindowMin={rtWindowMin} timeDomain={rtDomain} onRangeSelect={selectRtRange}
                                        displayMode="sync" forcedLeftAxisWidth={PAIRED_AXIS_WIDTH} forcedRightAxisWidth={rtRightWidth}
                                        onElementClick={onSelectTop || (lowerMode === 'sql' && onOpenSqlHistory) ? handleDimChartClick : undefined}
                                        legendHint={hintLower ? (k) => lowerHint(lowerMode, k) : undefined} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="dbmsperf-fade-swap dbmsperf-layout">
                        <div className="dbmsperf-toolbar">
                            <select className="dbmsperf-select" value={histRange ? 'custom' : String(historyDays)}
                                onChange={(e) => { const v = e.target.value; if (v === 'custom') return; setHistRange(null); setHistoryDays(v === 'all' ? 'all' : Number(v)); }}>
                                {getDaysOptions(t).map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                                <option value="all">{t('monitoring:range.all')}</option>
                                {histRange && <option value="custom">{t('monitoring:range.custom', { range: rangeLabel(histRange) })}</option>}
                            </select>
                            {histRange ? (
                                <button type="button" className="dbmsperf-btn dbmsperf-btn-secondary dbmsperf-range-clear" onClick={() => setHistRange(null)} title={t('monitoring:range.clearHint')}>
                                    <FiX size={12} />{t('monitoring:range.clear')}
                                </button>
                            ) : <span className="dbmsperf-toolbar-hint">{t('monitoring:range.dragHint')}</span>}
                            {renderInstRibbon([histWaitByInst, histMetricByInst, histDimByInst])}
                            <WaitClassSelect value={historyWaitClass} onChange={setHistoryWaitClass} t={t} />
                            <WaitSearch value={histSearch} onChange={typeHistSearch} suggestions={suggestionsFor(historyWaitClass, historyWaitSeries, historyWaitClassMap)} t={t} />
                            {renderWaitInfoButton()}
                            <button className="dbmsperf-btn dbmsperf-btn-secondary" onClick={() => { fetchHistoryMetric(); fetchHistoryWait(); }} disabled={historyMetricLoading}>
                                <FiRefreshCw size={12} className={historyMetricLoading ? 'dbmsperf-spin' : ''} />{t('monitoring:perf.refresh')}
                            </button>
                            <span className="dbmsperf-toolbar-note">
                                <FiInfo size={12} /> {t('monitoring:perf.note.awrLicense')}
                            </span>
                        </div>

                        {/* 대기이벤트를 위에 둔다 — t('monitoring:perf.whatWaited') 가 먼저이고,
                            메트릭은 그 원인을 뒷받침하는 보조 지표다.
                            두 차트 모두 displayMode="sync" + 같은 timeWindowMin 이라 x 축이 정렬된다. */}
                        <div className="dbmsperf-chart-card">
                            <div className="dbmsperf-chart-title dbmsperf-chart-title-fixed">
                                <span className="dbmsperf-chart-title-text" onMouseEnter={showFullTitle}>
                                    {t('monitoring:perf.h.waitHistory')}
                                    {historyWaitLoading && <span className="dbmsperf-chart-note"> {t('monitoring:perf.loading')}</span>}
                                    <span className="dbmsperf-chart-note"> {t(histScale === 'pct' ? 'monitoring:perf.note.waitClass' : histScale === 'avg' ? 'monitoring:perf.note.waitClassAvg' : 'monitoring:perf.note.waitClassTime')}</span>
                                    <span className="dbmsperf-chart-note dbmsperf-click-hint"> {t('monitoring:perf.pickEventHint')}</span>
                                    {histSearch && <span className="dbmsperf-chart-note dbmsperf-search-note"> {t(histExact ? 'monitoring:perf.search.activeExact' : 'monitoring:perf.search.active', { q: histSearch })}</span>}
                                    {histWaitsLine && <span className="dbmsperf-chart-note"> {t('monitoring:perf.waits.histNote', { event: histWaitTarget })}</span>}
                                    {histMulti.length > 0 && <span className="dbmsperf-chart-note dbmsperf-search-note"> {t('monitoring:perf.others.active', { n: histMulti.length })}</span>}
                                </span>
                                {histMulti.length > 0 && <button type="button" className="dbmsperf-metric-edit-btn dbmsperf-multi-chip" onClick={resetHistMulti} title={t('monitoring:perf.others.reset')} aria-label={t('monitoring:perf.others.reset')}>{t('monitoring:perf.others.chip', { n: histMulti.length })} <FiX size={11} /></button>}
                                {renderHintToggle('upper', hintUpper, setHintUpper)}
                                {renderScaleSeg(histScale, setHistScale, 'hist')}
                                <QueryViewButton onClick={() => setQvSlot('histWait')} />
                            </div>
                            {renderInstCharts(histWaitCharts, 200, (data, h, keys) => (
                                <PanelChart {...histWaitY} panel={historyWaitPanelFor(keys)} data={data} height={h} isDarkMode={darkMode} timeWindowMin={historyWindowMin} timeDomain={histDomain} onRangeSelect={selectHistRange}
                                    displayMode="sync" forcedLeftAxisWidth={PAIRED_AXIS_WIDTH} forcedRightAxisWidth={histRightWidth} onElementClick={handleHistWaitClick}
                                    legendHint={hintUpper ? (k) => waitHint(k, histClassAll) : undefined} />
                            ))}
                            <WaitOthersPicker open={othersOpen === 'hist'} drawn={historyWaitSeries.filter(k => k !== OTHERS_KEY_STR)} othersList={histOthers}
                                picked={histMulti} onApply={applyHistMulti} onReset={resetHistMulti} onClose={() => setOthersOpen(null)} t={t} />
                        </div>

                        {/* 아래 그래프 고르기(2026-10-18) — 실시간 성능과 같은 다섯 갈래. 고른 대기가 있으면 그 대기에 대한 것이다 */}
                        <div className="dbmsperf-picked">
                            {histPicked ? <span className="dbmsperf-picked-chip">{t('monitoring:perf.picked', { event: histPicked })}{histClass ? ` · ${histClass}` : ''}</span>
                                : <span className="dbmsperf-collapse-note">{t('monitoring:perf.pickedNone')}</span>}
                            {renderHintToggle('lower', hintLower, setHintLower)}
                            <span className="dbmsperf-seg" role="tablist" aria-label={t('monitoring:perf.lower.label')}>
                                {getLowerModes(t).map(o => (
                                    <button key={o.value} type="button" role="tab" aria-selected={histLowerMode === o.value}
                                        className={`dbmsperf-seg-btn${histLowerMode === o.value ? ' on' : ''}`} onClick={() => setHistLowerMode(o.value)}>
                                        {o.label}
                                    </button>
                                ))}
                            </span>
                            <QueryViewButton onClick={() => setQvSlot(histLowerMode === 'metric' ? 'histMetric' : 'histLower')} />
                        </div>
                        {histLowerMode === 'settings' ? (
                            <div className="dbmsperf-chart-card dbmsperf-fade-swap" key="hist-lower-settings">
                                {renderRelatedSettings({ picked: histPicked, data: histRelSettings, loading: histLowerLoading })}
                            </div>
                        ) : histLowerMode === 'metric' ? (
                        <div className="dbmsperf-chart-card dbmsperf-fade-swap" key="hist-lower-metric">
                            {renderMetricRibbon({ picked: histPicked, map: histMapping, cands: histCandidates, shown: histShown, toggle: toggleHistShown, ax: histAxes })}
                            <div className="dbmsperf-chart-title">
                                {t('monitoring:perf.h.metricHistory')} {historyMetricLoading && t('monitoring:perf.loading')}
                                {metricScope === 'pdb' && <span className="dbmsperf-chart-note" title={t('monitoring:perf.pdbScopeHint')}> {t('monitoring:perf.pdbScope')}</span>}
                                {histShown.length > 0 && <span className="dbmsperf-chart-note"> {histAxes.right.length ? t('monitoring:perf.twoAxes', { left: histAxes.leftUnit || '-', right: histAxes.rightUnit || '-' }) : (histAxes.leftUnit ? t('monitoring:perf.oneAxis', { unit: histAxes.leftUnit }) : '')}</span>}
                            </div>
                            {histShown.length ? (
                                renderInstCharts(histMetricCharts, 200, (data, h) => (
                                    <PanelChart {...histMetricY} panel={sysmetricPanelHistory} data={data} height={h} isDarkMode={darkMode} timeWindowMin={historyWindowMin} timeDomain={histDomain} onRangeSelect={selectHistRange}
                                        displayMode="sync" forcedLeftAxisWidth={PAIRED_AXIS_WIDTH} forcedRightAxisWidth={histRightWidth}
                                        legendHint={hintLower ? (k) => lowerHint('metric', k) : undefined} />
                                ))
                            ) : <div className="dbmsperf-empty-note">{t('monitoring:perf.ribbon.pick')}</div>}
                        </div>
                        ) : (
                            <div className="dbmsperf-chart-card dbmsperf-fade-swap" key={`hist-lower-${histLowerMode}`}>
                                <div className="dbmsperf-chart-title">
                                    {histPicked
                                        ? t('monitoring:perf.h.dimForEvent', { dim: getLowerModes(t).find(o => o.value === histLowerMode)?.label, event: histPicked })
                                        : t('monitoring:perf.aasByDim', { dim: getLowerModes(t).find(o => o.value === histLowerMode)?.label })}
                                    <span className="dbmsperf-chart-note" title={t('monitoring:perf.histAsh.hint')}> {t('monitoring:perf.histAsh.note')}</span>
                                    {histLowerLoading && <span className="dbmsperf-collapse-note">{t('monitoring:perf.loading')}</span>}
                                </div>
                                {histDimSeries.length ? renderInstCharts(histDimCharts, 220, (data, h) => (
                                    <PanelChart {...histDimY} panel={histDimPanel} data={data} height={h} isDarkMode={darkMode} timeWindowMin={historyWindowMin} timeDomain={histDomain} onRangeSelect={selectHistRange}
                                        displayMode="sync" forcedLeftAxisWidth={PAIRED_AXIS_WIDTH} forcedRightAxisWidth={histRightWidth}
                                        onElementClick={histLowerMode === 'sql' && onOpenSqlHistory ? handleHistDimClick : undefined}
                                        legendHint={hintLower ? (k) => lowerHint(histLowerMode, k) : undefined} />
                                )) : !histLowerLoading && <div className="dbmsperf-empty-note">{t('monitoring:perf.histAsh.empty')}</div>}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'lockwait' && (
                    <div className="dbmsperf-fade-swap dbmsperf-layout dbmsperf-lock-layout">
                        <div className="dbmsperf-toolbar">
                            {isLockHist ? (
                                <select className="dbmsperf-select" value={String(lockDays)} onChange={(e) => setLockDays(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                                    {getDaysOptions(t).map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                                    <option value="all">{t('monitoring:range.all')}</option>
                                </select>
                            ) : (
                                <select className="dbmsperf-select" value={lockWindowMin} onChange={(e) => setLockWindowMin(Number(e.target.value))}>
                                    {getWindowOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            )}
                            {/* 그래프에서 끌어 고른 구간 — 트리가 이 구간만 본다 */}
                            {lockRange ? (
                                <span className="dbmsperf-range-chip">
                                    {isLockHist
                                        ? t('monitoring:perf.lockRange', { from: wallText(lockRange.fromMs).slice(5, 16).replace('T', ' '), to: wallText(lockRange.toMs).slice(5, 16).replace('T', ' ') })
                                        : t('monitoring:perf.lockRange', { from: hms(wallText(lockRange.fromMs)), to: hms(wallText(lockRange.toMs)) })}
                                    <button type="button" onClick={() => setLockRange(null)} aria-label={t('monitoring:perf.lockRangeClear')} title={t('monitoring:perf.lockRangeClear')}><FiX size={11} /></button>
                                </span>
                            ) : (
                                <span className="dbmsperf-toolbar-hint">{t('monitoring:perf.lockRangeHint')}</span>
                            )}
                            {renderInstRibbon([lockTrendByInst])}
                            <QueryViewButton onClick={() => setQvSlot('lock')} />
                            <button className="dbmsperf-btn dbmsperf-btn-secondary" onClick={() => { fetchLockTree(); fetchLockTrend(); }} disabled={lockLoading}>
                                <FiRefreshCw size={12} className={lockLoading ? 'dbmsperf-spin' : ''} />{t('monitoring:perf.refresh')}
                            </button>
                            {!isLockHist && (
                                <label className="dbmsperf-autorefresh">
                                    <input type="checkbox" checked={lockAutoRefresh} onChange={(e) => setLockAutoRefresh(e.target.checked)} />
                                    {t('monitoring:perf.autoRefresh30')}
                                </label>
                            )}
                            <span className="dbmsperf-toolbar-note">
                                <FiInfo size={12} /> {t(isLockHist ? 'monitoring:perf.lockHist.chainScan' : 'monitoring:perf.note.chainScan')}
                            </span>
                        </div>

                        {/* 락 종류 — 그래프 위. 고른 것이 그래프와 트리에 함께 걸린다(서버가 거른다). 기본 TX·TM */}
                        <div className="dbmsperf-locktype-filter">
                            <span className="dbmsperf-label">{t('monitoring:perf.lockType')}</span>
                            <button type="button" className="dbmsperf-info-btn" title={t('monitoring:lockInfo.open')} aria-label={t('monitoring:lockInfo.open')} onClick={() => setShowLockInfo(true)}>
                                <FiInfo size={13} />
                            </button>
                            {/* 이 구간에 나타난 종류만, 묶음(DML·공간·시스템·메모리 경합·기타)으로 */}
                            {lockTypeGroups.length === 0 && !lockLoading && <span className="dbmsperf-empty-note">{t('monitoring:perf.lockTypeNone')}</span>}
                            {lockTypeGroups.map(g => (
                                <span key={g.key} className="dbmsperf-locktype-group" title={t(`monitoring:lockInfo.group.${g.key}.desc`)}>
                                    <span className="dbmsperf-locktype-group-name">{t(`monitoring:lockInfo.group.${g.key}.name`)}</span>
                                    {g.items.map(({ type, sessions }) => (
                                        <label
                                            key={type}
                                            className={`dbmsperf-locktype-check ${lockTypeOn(type) ? 'on' : ''}`}
                                            title={lockDescOf(t, type) || getLockTypeDescriptions(t)[type] || t('monitoring:perf.noDesc')}
                                        >
                                            <input type="checkbox" checked={lockTypeOn(type)} onChange={() => toggleLockTypeFilter(type)} />
                                            {type}
                                            <span className="dbmsperf-locktype-count" title={t('monitoring:perf.lockTypeCountHint')}>{sessions}</span>
                                        </label>
                                    ))}
                                </span>
                            ))}
                            {lockTypeAbsent.length > 0 && (
                                <span className="dbmsperf-toolbar-hint">{t('monitoring:perf.lockTypeAbsent', { types: lockTypeAbsent.join(', ') })}</span>
                            )}
                            <button type="button" className="dbmsperf-locktype-clear" onClick={() => setLockTypeFilter('ALL')} disabled={lockTypeFilter === 'ALL'}>
                                {t('monitoring:perf.lockTypeAll')}
                            </button>
                            <button type="button" className="dbmsperf-locktype-clear" onClick={() => setLockTypeFilter(DEFAULT_LOCK_TYPES)}
                                disabled={lockTypeFilter !== 'ALL' && lockTypeFilter.length === 2 && DEFAULT_LOCK_TYPES.every(x => lockTypeFilter.includes(x))}>
                                {t('monitoring:perf.lockTypeDefault')}
                            </button>
                        </div>

                        {/* 추이 — AAS(락을 기다린 평균 세션 수). CPU 를 쓰지 않아도 DB time 에 쌓이므로 「얼마나 많은 사용자가 멈춰 있었나」 다.
                            노드가 여럿이면 노드마다 그린다(같은 가로축). 끌어서 구간을 고르면 아래 트리가 그 구간만 본다 */}
                        <div className="dbmsperf-chart-card">
                            <div className="dbmsperf-chart-title dbmsperf-chart-title-fixed">
                                <span className="dbmsperf-chart-title-text" onMouseEnter={showFullTitle}>
                                    {t('monitoring:perf.h.lockTrend')}
                                    <span className="dbmsperf-chart-note"> {t(`monitoring:perf.lockScale.note.${lockScale}`)}</span>
                                    {isLockHist && <span className="dbmsperf-chart-note"> {t('monitoring:perf.lockHist.sampleNote')}</span>}
                                    {lockTrendLoading && t('monitoring:perf.loading')}
                                </span>
                                {renderLockScaleSeg()}
                            </div>
                            {/* 노드마다 한 장 · 고른 노드만 · 같은 세로축(끌면 함께) — 실시간 성능의 대기이벤트 추이와 같은 장치 */}
                            {renderInstCharts(lockCharts, 170, (data, h, keys) => (
                                <PanelChart
                                    {...lockY}
                                    panel={lockTrendPanelFor(keys)} data={data} height={h} isDarkMode={darkMode}
                                    timeWindowMin={lockWinMin} timeDomain={lockDomain} displayMode="sync"
                                    forcedLeftAxisWidth={PAIRED_AXIS_WIDTH}
                                    onRangeSelect={(a, b) => { if (isLockHist && b - a < MIN_DRAG_MS.hist) return; setLockRange({ fromMs: a, toMs: b }); }}
                                    selectedRange={lockRange ? [lockRange.fromMs, lockRange.toMs] : null}
                                />
                            ))}
                        </div>

                        <div className="dbmsperf-chart-card dbmsperf-lock-tree-card">
                            <div className="dbmsperf-chart-title">
                                {t('monitoring:perf.h.lockTree')}
                                {lockTree.length > 0 && (
                                    <span className="dbmsperf-chart-note">
                                        {' '}{t('monitoring:perf.chainCount', { chains: lockTree.length, sessions: lockNodeCount })}
                                        {' · '}{t('monitoring:perf.lockSortNote')}
                                    </span>
                                )}
                                {lockLoading && t('monitoring:perf.loading')}
                            </div>
                            {lockTree.length > 0 && (
                                <div className="dbmsperf-lock-scope-note">
                                    <FiInfo size={11} /> {lockRange ? t('monitoring:perf.lockScopeRange')
                                        : isLockHist ? t('monitoring:perf.lockHist.scope', { range: lockMeta.range ? rangeLabel(lockMeta.range) : '' })
                                            : t('monitoring:perf.lockScopeWindow', { min: lockWindowMin })}
                                    {lockMeta.truncated && <> · {t('monitoring:perf.lockTruncated')}</>}
                                </div>
                            )}
                            {!lockLoading && lockTree.length === 0 && (
                                <div className="dbmsperf-empty-note">{t('monitoring:perf.noLockWaitsForType')}</div>
                            )}
                            {/* 트리 2 : SQL 원문 1 — 각자 스크롤한다(창 전체는 스크롤하지 않는다) */}
                            <div className="dbmsperf-lock-split">
                                <div className="dbmsperf-lock-tree-pane">
                                    {filteredLockTree.length > 0 && (
                                        <ul className="dbmsperf-locktree-root">
                                            {filteredLockTree.map(renderLockNode)}
                                        </ul>
                                    )}
                                </div>
                                <div className="dbmsperf-lock-sql-pane">
                                    <div className="dbmsperf-lock-sql-head">
                                        <span>{lockSql ? t('monitoring:perf.lockSql.title', { sqlId: lockSql.sqlId }) : t('monitoring:perf.lockSql.heading')}</span>
                                        {lockSql && (
                                            <button type="button" className="dbmsperf-lock-sql-close" onClick={() => setLockSql(null)} title={t('monitoring:perf.lockSql.close')}>
                                                <FiX size={11} />
                                            </button>
                                        )}
                                    </div>
                                    {!lockSql ? (
                                        <div className="dbmsperf-empty-note">{t('monitoring:perf.lockSql.pick')}</div>
                                    ) : lockSql.loading ? (
                                        <div className="dbmsperf-empty-note">{t('monitoring:perf.loading')}</div>
                                    ) : lockSql.error ? (
                                        <div className="dbmsperf-empty-note"><FiInfo size={12} /> {lockSql.error}</div>
                                    ) : lockSql.text == null ? (
                                        <div className="dbmsperf-empty-note">{t('monitoring:perf.lockSql.gone')}</div>
                                    ) : (
                                        <>
                                            {lockSql.source === 'awr' && <div className="dbmsperf-lock-sql-src"><FiInfo size={11} /> {t('monitoring:perf.lockSql.fromAwr')}</div>}
                                            <SqlSourceView key={lockSql.sqlId} className="dbmsperf-lock-sql-body" sql={lockSql.text} loadingText="" />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    if (embedded) return body;

    return (
        <AppModal windowed
            open
            onClose={onClose}
            icon={<FiBarChart2 size={14} />}
            title={t('monitoring:perf.title')}
            overlayClassName="dbmsperf-overlay"
            className="dbmsperf-modal"
            headerClassName="dbmsperf-header"
            closeClassName="dbmsperf-close"
        >

                <div className="dbmsperf-tabs">
                    <button
                        className={`dbmsperf-tab ${activeTab === 'realtime' ? 'active' : ''}`}
                        onClick={() => setActiveTab('realtime')}
                    >
                        <FiActivity size={13} /> {t('monitoring:perf.tab.realtime')}
                    </button>
                    <button
                        className={`dbmsperf-tab ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FiClock size={13} /> {t('monitoring:perf.tab.history')}
                    </button>
                    <div className="dbmsperf-tabs-spacer" />
                    <select
                        className="dbmsperf-select dbmsperf-select-inline"
                        value={selectedConnId || ''}
                        onChange={(e) => setSelectedConnId(e.target.value)}
                    >
                        {oracleConns.length === 0 && <option value="">{t('monitoring:perf.noOracleConn')}</option>}
                        {oracleConns.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    <label className="dbmsperf-demo-toggle">
                        <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                        {t('monitoring:perf.demoData')}
                    </label>
                </div>

                {body}

                <div className="dbmsperf-footer">
                    <button className="app-btn dbmsperf-btn dbmsperf-btn-secondary" onClick={onClose}>{t('monitoring:perf.close')}</button>
                </div>
        </AppModal>
    );
};

export default DbmsPerfAnalysisModal;
